/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing chat-store actions gate feature behavior from runtime flags. */
import {
	type Action,
	type BoundActions,
	createActionsHook,
	createContainer,
	createHook,
	createStateHook,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
	type OverrideContainerComponent,
	type StoreActionApi,
} from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { getRovoParams, updatePageRovoParams } from '@atlaskit/rovo-triggers/params';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { A2UIMessage } from '@atlassian/a2ui-renderer/types/A2UIProtocol.types';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import type { BrowserContextSanitizer } from '@atlassian/conversation-assistant-chat-prompt-input/types';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import {
	type FollowUpSource,
	type InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	type ExperienceNameType,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	addSentryBreadcrumb,
	logException,
} from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { SCHEDULED_CHAT_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { AdditionalAnalyticAttributes } from '@atlassian/conversation-assistant-message-actions-api/types';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	type AgentVersionInput,
	type AlphaExtendedThinkingMode,
	type AssistantMessage,
	type ChatMessage,
	type ConversationChannelsResponse,
	type ConversationChannelScope,
	type Dialogues,
	type FetchStreamReturn,
	type Message,
	isActionExecutedSuccessfully,
	isAssistantMessage,
	isChannelNotFoundError,
	isConversationChannelDataMessage,
	isFollowUpMessage,
	isHumanMessage,
	isInProgressStreamResponse,
	isSSEMessage,
	isStreamAnswerPart,
	isStreamAUPError,
	isStreamError,
	isStreamReconnectSupportPart,
	isStreamResponse,
	isStreamTaskStatus,
	isStreamTodoSnapshot,
	isStreamTrace,
	type LastAgentMessageStatus,
	type MessageFileId,
	type MessageType,
	type ParsedSSEMessage,
	type RovoLabsConfirmedTools,
	type StreamMessage,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	MessageActionInvocationStatus,
	MessageActionPayload,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	createChatStore,
	EMPTY_CONVERSATION_ID,
} from '@atlassian/conversation-assistant-store/controllers/chat';
import { createProvisionalConversationAgent } from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import {
	type AgentMetadata,
	type AgentVersion,
	AnswerNowStatusType,
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	isAnswerNowAvailable,
	isStoreAssistant,
	isStoreError,
	isStoreFollowUp,
	isStoreHuman,
	StoreActionLoggableName,
	type StoreAssistant,
	type StoreChatItem,
	isTerminalTaskState,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { mapApiTaskStatusToProgressState } from '@atlassian/conversation-assistant-store/controllers/tasks-shared/task-helpers';

import { getChatLoadPreferenceKey } from '../../common/constants/getChatLoadPreferenceKey';
import { SHORTER_TIMEOUT_BEFORE_NEW_CHAT } from '../../common/constants/private';
import { StoreErrorFlagId } from '../../common/ui/hooks/use-show-store-error-flag/StoreErrorFlagId';
import { interactionMetrics } from '../../common/utils/browser-metrics/interactionMetrics';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { isRovoCustomAgent } from '../../common/utils/is-rovo-custom-agent';
import {
	buildToolsAttributeFromConfirmedTools,
	type ToolActionedAnalyticsAttribute,
} from '../analytics/tools-attributes';
import { redactToolKeyForPii } from '../analytics/tools-attributes/redactToolKeyForPii';
import { getLastMessageSentAt, setLastMessageSentAt } from '../last-message-sent';

import { createChatReconnectableStream } from './createChatReconnectableStream';
import { MessageActionError } from './error-classes/MessageActionError';
import { NoFinalResponseError } from './error-classes/NoFinalResponseError';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { buildUnifiedStreamTaskTarget, handleStreamTaskStatus } from './handle-stream-task-status';
import {
	getInteractionModeExecutedFromStoreAssistant,
	getInteractionModeRequested,
	interactionModeRequestedAttributes,
} from './interaction-mode';
import { mergeConversationWithNetworkPriority } from './mergeConversationWithNetworkPriority';
import { processAifcActionStreamMessage } from './process-aifc-action-stream-message';
import {
	type ReconnectToInProgressStreamParams,
	reconnectToInProgressStreamProvider,
} from './reconnect-action';
import {
	populateConfirmedToolsContext,
	processResearchStreamMessage,
	sendMessageActionProvider,
	type SendMessageConsumerPramas,
} from './send-message-action';
import { createStoreAbortController } from './store-abort-controller';
import {
	type ActionErrorHandler,
	type OnErrorCallback,
	storeErrorHandler,
} from './store-error-handler';
import type {
	ChatStoreAction,
	ChatStoreContainerProps,
	ChatStoreState,
	MessageSource,
	ResearchStreamState,
} from './types';
import {
	addAnswerPart,
	addAnswerPartWithMarkdownData,
	checkActiveConversationMatchesSource,
	clearStatus,
	clearStoreAssistantMessageContent,
	clearTracesIfNotEmpty,
	createStreamMarkdownProcessor,
	dedupeTraceMessagesWithLatest,
	createStoreAssistant,
	createStoreAssistantWithContent,
	createStoreError,
	createStoreHuman,
	createStoreHumanWithContent,
	deriveInvocationStatus,
	extractA2UIFromStructuredContent,
	extractActionableFollowUpsMarkdownData,
	extractMarkdownContent,
	findLastIndex,
	findStoreMessageIndex,
	generateId,
	generateTmpMessageId,
	getAgentMessageById,
	getAssociatedHumanMessageId,
	getConversation,
	getLastAgentMessageID,
	getSourceAgentId,
	getTraceFromStreamMessage,
	isEmptyConversation,
	isValidAdfJsonString,
	markInFlightActionsAsCancelled,
	markStreamingMessagesAsComplete,
	preprocessStreamResponseDetails,
	processStreamResponseMarkdown,
	replaceAnswerData,
	shouldPreprocessDetailsForConversation,
	updateStoreAssistantMessageAnswerNowStatus,
	updateStoreAssistantMessageId,
} from './utils';

type SendMessageExperienceNameParamsWithTaskMode = {
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	taskModeEnabled?: boolean;
	fastModeEnabled?: boolean;
	isCustomRovoAgent?: boolean;
};

const getSendMessageExperienceName = (
	params: SendMessageExperienceNameParamsWithTaskMode,
): ExperienceNameType => {
	if (params.isCustomRovoAgent) {
		return ExperienceName.SEND_MESSAGE_ROVO_CUSTOM_AGENT;
	}

	if (params.deepResearchEnabled) {
		return ExperienceName.SEND_DR_MESSAGE;
	}

	if (params.thinkDeeperEnabled) {
		return ExperienceName.SEND_TD_MESSAGE;
	}

	if (params.taskModeEnabled) {
		return ExperienceName.SEND_TASK_MODE_MESSAGE;
	}

	return ExperienceName.SEND_MESSAGE;
};

// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing chat-store baseline: narrow inline-view page params are adapted to the shared creation context type.
const HIGHLIGHT_ACTION_INLINE_VIEW_PAGE_CREATION_CONTEXT_PARAMS = {
	experience: 'inline_view',
	contentTypes: ['page'],
} as unknown as CreationContextParams;

const CONVERSATION_LIST_PAGE_SIZE = 25;
const CONVERSATION_LIST_PAGINATION_EXPERIMENT = 'rovo_chat_conversation_list_pagination';

const isConversationListPaginationEnabled = (): boolean =>
	FeatureGates.getExperimentValue(CONVERSATION_LIST_PAGINATION_EXPERIMENT, 'isEnabled', false);

/**
 * Resolve the agent to seed into `conversation.agent` for optimistic/seeded
 * conversation creation. Precedence:
 * - Under `rovo_chat_agent_selection`: the backend-returned agent if present,
 *   otherwise a provisional agent built from the known agent id (so the header,
 *   which reads only `conversation.agent.agent_id`, can hydrate it).
 * - Otherwise (legacy): the backend agent only when `rovo_chat_chat_history_refresh`
 *   is on; else no seed.
 *
 * rovo_chat_agent_selection cleanup: drop the gate and always seed from
 * `backendAgent ?? createProvisionalConversationAgent(agentId)`.
 */
const resolveConversationSeedAgent = ({
	backendAgent,
	agentId,
	legacyAgent,
}: {
	backendAgent?: AgentMetadata;
	agentId?: string;
	legacyAgent?: AgentMetadata;
}): AgentMetadata | undefined => {
	if (fg('rovo_chat_agent_selection')) {
		return backendAgent ?? createProvisionalConversationAgent(agentId);
	}
	return legacyAgent && fg('rovo_chat_chat_history_refresh') ? legacyAgent : undefined;
};

/**
 * Util action to initialize conversation with conversationId
 */
const initializeConversationData =
	({ conversationId, data }: { conversationId: string; data: Conversation }): ChatStoreAction =>
	({ getState, setState }) => {
		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...(getState().conversations[conversationId] ?? {}),
					...data,
				},
			},
		});
	};

/**
 * Util action to allow updating the conversation based on `conversationId`
 * without needing to keep spreading the existing
 *
 * Similar to `initializeConversationData`, the difference is, it will not allow the the data to be updated if there is no existing conversation
 */
const setConversationData =
	({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Partial<Conversation>;
	}): ChatStoreAction =>
	({ getState, setState }) => {
		if (!getState().conversations[conversationId]) {
			return;
		}

		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...(getState().conversations[conversationId] ?? {}),
					...data,
				},
			},
		});
	};

/**
 * Reset an existing conversation into the loading state ahead of a (re)fetch.
 *
 * This is a partial patch of only the fields the loading reset actually changes:
 * status, messages, traces and sourceAgentId. Every other field —
 * notably `conversation.agent` (the header's display identity), `name`, `id` and
 * `timeCreated` — is preserved automatically by the shallow merge, so callers do not
 * need to re-copy carry-over fields or special-case agent preservation behind a gate.
 *
 * No-ops if the conversation does not exist (callers always guard for an existing,
 * non-empty, idle conversation before resetting).
 */
const resetConversationForLoading =
	({
		conversationId,
		traces,
		sourceAgentId,
	}: {
		conversationId: string;
		traces: Conversation['traces'];
		sourceAgentId: Conversation['sourceAgentId'];
	}): ChatStoreAction =>
	({ getState, setState }) => {
		const existing = getState().conversations[conversationId];
		if (!existing) {
			return;
		}

		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...existing,
					status: CHAT_STORE_STATUS.loadingChat,
					messages: [],
					traces,
					sourceAgentId,
				},
			},
		});
	};

/**
 * Utils function to remove conversation from store
 */
const removeConversationFromStore =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	({ getState, setState }) => {
		const { [conversationId]: _, ...otherConversations } = getState().conversations;
		setState({
			conversations: otherConversations,
		});
	};

/**
 * Action Factory
 * Adds custom _middlewares_ to handle common cases and unexpected errors.
 * This is a hack to attach common logic to all actions.
 * It should not use the store action dispatcher. Instead, should call the actionCallback directly.
 *
 *
 * To add invocation-level error handling (`onError`):
 *
 * 1. Define actionargs type with `onError` using `OnErrorCallback`:
 *    ```ts
 *    type MyActionArgs = {
 *      myParam: string;
 *      onError?: OnErrorCallback<MyActionArgs>;
 *    };
 *    ```
 *
 * 2. Wire up `actionErrorHandler` to delegate to `onError`:
 *    ```ts
 *    createSafeAction(name, actionFn, {
 *      actionErrorHandler: (context) => {
 *        const { onError, ...args } = context.args?.[0] ?? {};
 *			if (onError) {
 *				return onError({ ...context, args });
 *			}
 *			// default action level error handling
 *			return { proceedToDefaultHandling: true };
 *      },
 *    });
 *    ```
 *
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const createSafeAction =
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	<T extends (...args: any[]) => any, R = void>(
		actionLoggableName: StoreActionLoggableName,
		actionHOF: T,
		props?: {
			experienceName?: (args: Parameters<T>) => ExperienceNameType;
			getRateLimitState?: (args: Parameters<T>) => { isDeepResearchAttempt?: boolean };
			actionErrorHandler?: ActionErrorHandler<Parameters<T>>;
		},
	) =>
		(...args: Parameters<T>): Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<R>> =>
		async (api: StoreActionApi<ChatStorePrivateState>, containerProps: ChatStoreContainerProps) => {
			/*
		  Add custom logic here
		 */
			// General try/catch statement to handle unexpected errors
			try {
				addSentryBreadcrumb(`createSafeAction: executing ${actionLoggableName}`);
				// Here we call the action callback
				// Same as dispatch(actionHOF)
				const action = actionHOF(...args);
				// Here we execute the action i.e. what happens internally when the action is dispatched
				const result = await action(api, containerProps);
				return result as R;
			} catch (error: unknown) {
				storeErrorHandler({
					args,
					api,
					actionLoggableName,
					error,
					containerProps,
					setConversationData,
					experienceName: props?.experienceName,
					getRateLimitState: props?.getRateLimitState,
					actionErrorHandler: props?.actionErrorHandler,
				});
				return undefined as R;
			}
		};

/**
 * ************************************************************
 * Private Actions
 * ************************************************************
 */

const addStoreChatItem = createSafeAction(
	StoreActionLoggableName.addStoreChatItem,
	({
		message,
		conversationId: conversationIdParam,
	}: {
		message: StoreChatItem;
		conversationId?: string;
	}): ChatStoreAction =>
		({ getState }, { setMessages, getMessages }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			setMessages(conversationId, [...getMessages(conversationId), message]);
		},
);

const addHumanMessage = createSafeAction(
	StoreActionLoggableName.addHumanMessage,
	({
		id,
		content,
		conversationId: conversationIdParam,
		isFromSeededConversation = false,
		requestId,
		fileUploads,
	}: {
		id: string;
		content: DocNode;
		conversationId?: string;
		isFromSeededConversation?: boolean;
		requestId?: string;
		fileUploads?: MessageFileId[];
	}): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			dispatch(
				addStoreChatItem({
					message: createStoreHuman(id, content, isFromSeededConversation, requestId, fileUploads),
					conversationId,
				}),
			);
		},
);

const addAssistantMessage = createSafeAction(
	StoreActionLoggableName.addAssistantMessage,
	({
		id,
		content,
		conversationId: conversationIdParam,
		agentNamedId,
		agentAuthorId,
	}: {
		id: string;
		content: string | DocNode;
		conversationId?: string;
		agentNamedId?: string;
		agentAuthorId?: string;
	}): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const assistantMessageParams = {
				id,
				content,
				agentNamedId,
				agentAuthorId,
				conversationId,
			};

			dispatch(
				addStoreChatItem({
					message: createStoreAssistant(assistantMessageParams),
					conversationId,
				}),
			);
		},
);

/**
 * ************************************************************
 * Public Actions
 * ************************************************************
 */

const clearFollowUpMessages = createSafeAction(
	StoreActionLoggableName.clearFollowUpMessages,
	({ conversationId: conversationIdParam }: { conversationId?: string }): ChatStoreAction =>
		({ getState }, { getMessages, setMessages }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const filteredMessages = messages.filter((message) => !isStoreFollowUp(message));

			setMessages(conversationId, filteredMessages);
		},
);

/**
 * Send a message to the assistant and add it to react-sweet-state
 *
 * @param agentId -- this parameter will be prioritised over agent.id
 */
const sendMessage = createSafeAction(
	StoreActionLoggableName.sendMessage,
	(props: SendMessageConsumerPramas): ChatStoreAction =>
		sendMessageActionProvider({
			...props,
			setConversationData,
			createConversationOptimistically,
			addHumanMessage,
			addAssistantMessage,
			clearFollowUpMessages,
			setSelectedConversation,
			reconnectToInProgressStream,
		}),
	{
		experienceName: (args) =>
			getSendMessageExperienceName({
				deepResearchEnabled: args[0]?.deepResearchEnabled,
				thinkDeeperEnabled: args[0]?.thinkDeeperEnabled,
				taskModeEnabled: args[0]?.taskModeEnabled,
				fastModeEnabled: args[0]?.fastModeEnabled,
			}),
		getRateLimitState: (args) => ({
			isDeepResearchAttempt: args[0]?.deepResearchEnabled,
		}),
	},
);

/**
 * Similar action to `sendMessage` with the barebone functionalities to confirm tools/skills/actions
 *
 * Initially this will assume that this function only created on existing conversation
 */
const sendMessageConfirmedTools = createSafeAction(
	StoreActionLoggableName.sendMessageConfirmedTools,
	({
		conversationId: conversationIdParam,
		agentId,
		confirmed_tools,
		messageSources,
		storeUserMessages = false,
		webSearchEnabled,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		taskModeEnabled,
		includeAIFeatureContext,
		agentVersion,
		actionedAnalyticsAttributes,
		resolutionType,
		productKey,
		addCurrentPageAsContextUserPreference,
		browserContextSanitizer,
		hasStagedContent,
	}: {
		conversationId?: string;
		agentId?: string;
		confirmed_tools: RovoLabsConfirmedTools;
		messageSources?: Partial<MessageSource>;
		storeUserMessages?: boolean;
		webSearchEnabled?: boolean;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
		fastModeEnabled?: boolean;
		taskModeEnabled?: boolean;
		includeAIFeatureContext?: boolean;
		agentVersion?: AgentVersionInput;
		actionedAnalyticsAttributes?: AdditionalAnalyticAttributes;
		resolutionType?: 'confirmed' | 'cancelled';
		/**
		 * The following context-driving params are used (behind the
		 * `rovo-include-full-context-on-confirmed-tools` experiment) to build the same
		 * full request context that `sendMessage` sends, so the backend receives the
		 * creation/page/whiteboard/etc context on tool confirmation. All optional.
		 */
		productKey?: string;
		addCurrentPageAsContextUserPreference?: boolean;
		browserContextSanitizer?: BrowserContextSanitizer;
		/** Whether the staging area holds a created draft the user can refine. with disambiguation, its possible to be in the CWR staging area but have no content previewed yet. */
		hasStagedContent?: boolean;
	}): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				chatContextStoreActions,
				experienceTracker,
				getMessages,
				setMessages,
				trackAIMAUEvent,
				trackAgentEvent,
				processStreamResearch,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				clearMessageResearch,
				updateResearchMessageId,
				markIncompleteResearchesErrored,
				sendAnalyticsEvent,
				getIsChatMounted,
				capabilities,
				unifiedTasksActions,
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamActionClassification,
				onTempMessageIdReplaced,
			},
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const resolvedAgentVersion =
				agentVersion ?? getState().conversations[conversationId]?.agentVersion;
			const confirmedToolsExperienceName = taskModeEnabled
				? ExperienceName.SEND_CONFIRMED_TOOLS_TASK_MODE_MESSAGE
				: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE;

			// Collection of callbacks for experience and performance tracking
			const expAndPerfTrackers = {
				start: () => {
					experienceTracker.start({
						name: confirmedToolsExperienceName,
						id: conversationId,
						attributes: {
							conversationId,
							interactionSource: messageSources?.interactionSource,
						},
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.start();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.start();
				},
				firstAnswerPartReceived: () => {
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
				succeed: () => {
					experienceTracker.succeed({
						name: confirmedToolsExperienceName,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
				},
				aupError: () => {
					experienceTracker.succeed({
						name: confirmedToolsExperienceName,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
				agentDeactivated: ({ error }: { error: ApiError }) => {
					experienceTracker.fail({
						name: confirmedToolsExperienceName,
						error,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.cancel();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.cancel();
				},
				abort: () => {
					experienceTracker.abort({
						name: confirmedToolsExperienceName,
						reason: 'Aborted',
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.cancel();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.cancel();
				},
				fail: ({ error }: { error: Error }) => {
					experienceTracker.fail({
						name: confirmedToolsExperienceName,
						error: error,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
			};

			const requestedModeAttributes = interactionModeRequestedAttributes(
				getInteractionModeRequested({
					deepResearchEnabled,
					thinkDeeperEnabled,
					fastModeEnabled,
					taskModeEnabled,
				}),
			);

			const trackingAttributes: {
				requestId?: string;
				messageId?: string;
				humanMessageId?: string;
				instrumentationId?: string;
				initiatedTimestamp?: string;
				interactionModeExecuted?: string;
				isRovoCustomAgent: boolean | undefined;
				sendMessageReason: 'TOOLS_CONFIRMATION';
				scenarioId?: string;
				aiFeatureName?: string;
			} = {
				isRovoCustomAgent: isRovoCustomAgent(agentId),
				sendMessageReason: 'TOOLS_CONFIRMATION',
				aiFeatureName: messageSources?.aiFeatureName,
			};

			// Source executed mode from the originating assistant message (the one this
			// confirmed-tools action is being executed against). `confirmed_tools.message_id`
			// is the assistant message id whose tool calls the user just confirmed.
			const originatingAssistantMessage = getMessages(conversationId).find(
				(item): item is StoreAssistant =>
					isStoreAssistant(item) &&
					item.content?.message?.message?.id === confirmed_tools.message_id,
			);
			const interactionModeExecutedFromOriginatingAssistantMessage =
				getInteractionModeExecutedFromStoreAssistant(originatingAssistantMessage);

			const isCancelled = resolutionType === 'cancelled';

			const tools: ToolActionedAnalyticsAttribute[] =
				buildToolsAttributeFromConfirmedTools(confirmed_tools);

			const agentAnalyticsAttributes: {
				touchPoint: string;
				tools: ToolActionedAnalyticsAttribute[];
			} = {
				touchPoint: 'chatSendMessageConfirmedTools',
				tools,
			};

			// Collection of callbacks for analytics tracking
			const analyticsTrackers = {
				/**
				 * There are 2 AIMAU events here, because in resumption we're doing 2 things
				 * 1. We're confirming the previous tools result
				 * 2. And the confirmation triggers a LLM chat streaming
				 */
				initiated: ({ timestamp }: { timestamp?: string }) => {
					const actionKey = redactToolKeyForPii({
						toolDefinitionSource: confirmed_tools.tools[0]?.tool_source,
						key: confirmed_tools.tools[0]?.key,
					});
					trackAIMAUEvent({
						...trackingAttributes,
						type: 'actioned',
						actionResult: isCancelled ? 'toolsExecutionCancelled' : 'toolsExecutionConfirmed',
						proactive: false,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						attributes: {
							...(fg('jira-ai-agentic-planning-technical-changes')
								? actionedAnalyticsAttributes
								: {}),
							...(interactionModeExecutedFromOriginatingAssistantMessage
								? {
										interactionModeExecuted: interactionModeExecutedFromOriginatingAssistantMessage,
									}
								: {}),
							...(actionKey ? { actionKey } : {}),
							...(trackingAttributes.aiFeatureName && {
								aiFeatureName: trackingAttributes.aiFeatureName,
							}),
						},
						// override the messageId to the confirmed_tools.message_id and not what's in trackingAttributes,
						// because action is on the original message not this message
						messageId: confirmed_tools.message_id,
					});

					trackAIMAUEvent({
						...trackingAttributes,
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp,
						attributes: requestedModeAttributes,
					});

					// @deprecated kept for Data Science (DS) consumers. Replaced by backend unified instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
					if (agentId && isRovoCustomAgent(agentId)) {
						trackAgentEvent({
							action: isCancelled ? 'toolsExecutionCancelled' : 'toolsExecutionConfirmed',
							actionSubject: 'rovoAgent',
							attributes: {
								...trackingAttributes,
								...agentAnalyticsAttributes,
								agentId,
								singleInstrumentationId: trackingAttributes.instrumentationId,
								...(fg('jira-ai-agentic-planning-technical-changes') &&
									actionedAnalyticsAttributes),
							},
						});
					}
				},
				aborted: () => {
					trackAIMAUEvent({
						...trackingAttributes,
						type: 'dismissed',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						messageId: trackingAttributes.messageId,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						attributes: requestedModeAttributes,
					});

					// @deprecated kept for Data Science (DS) consumers. Replaced by backend unified instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
					if (agentId && isRovoCustomAgent(agentId)) {
						trackAgentEvent({
							action: 'toolsExecutionStreamStopped',
							actionSubject: 'rovoAgent',
							attributes: {
								...trackingAttributes,
								...agentAnalyticsAttributes,
								agentId,
								singleInstrumentationId: trackingAttributes.instrumentationId,
							},
						});
					}
				},
				viewed: () => {
					trackAIMAUEvent({
						...trackingAttributes,
						type: 'viewed',
						proactive: false,
						userGenerated: true,
						source: messageSources?.interactionSource,
						messageId: trackingAttributes.messageId,
						subjectId: messageSources?.followUpSource,
						citedProductList: {},
						sourceProducts: {},
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp: new Date().toISOString(),
						attributes: {
							...(trackingAttributes.interactionModeExecuted
								? { interactionModeExecuted: trackingAttributes.interactionModeExecuted }
								: {}),
							...(trackingAttributes.aiFeatureName && {
								aiFeatureName: trackingAttributes.aiFeatureName,
							}),
						},
					});

					// @deprecated kept for Data Science (DS) consumers. Replaced by backend unified instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
					if (agentId && isRovoCustomAgent(agentId)) {
						trackAgentEvent({
							action: 'toolsExecutionResultViewed',
							actionSubject: 'rovoAgent',
							attributes: {
								...trackingAttributes,
								...agentAnalyticsAttributes,
								agentId,
								singleInstrumentationId: trackingAttributes.instrumentationId,
								scenarioId: trackingAttributes.scenarioId,
							},
						});
					}
				},
				error: () => {
					trackAIMAUEvent({
						...trackingAttributes,
						type: 'error',
						proactive: false,
						userGenerated: true,
						messageId: trackingAttributes.requestId,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp: new Date().toISOString(),
						attributes: requestedModeAttributes,
					});

					// @deprecated kept for Data Science (DS) consumers. Replaced by backend unified instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
					if (agentId && isRovoCustomAgent(agentId)) {
						trackAgentEvent({
							action: 'toolsExecutionResultError',
							actionSubject: 'rovoAgent',
							attributes: {
								...trackingAttributes,
								...agentAnalyticsAttributes,
								agentId,
								singleInstrumentationId: trackingAttributes.instrumentationId,
							},
						});
					}
				},
			};

			const updateCurrentConversation = (data: Partial<Conversation>) => {
				dispatch(
					setConversationData({
						conversationId,
						data,
					}),
				);
			};

			const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();

			let hasReceivedFinalResponse = false;
			let generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>> | undefined;
			let responseData: FetchStreamReturn<StreamMessage, boolean>['responseData'] | undefined;
			let hasLoggedInitiatedEvent = false;
			// Hoisted so it is accessible in the catch block for clearMessageResearch
			let tmpAssistantMessageId = generateTmpMessageId(conversationId);
			// Reconnect state — populated once we receive the conversation channel data message
			let agentMessageId: string | undefined;
			let isReconnectSupported = false;

			const handleAbort = () => {
				expAndPerfTrackers.abort();
				confirmAbort();
				clearMessageResearch({ messageId: tmpAssistantMessageId });
				!hasLoggedInitiatedEvent &&
					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.aborted();

				// Call the cancel API to stop backend processing
				if (
					trackingAttributes.messageId &&
					trackingAttributes.messageId !== tmpAssistantMessageId
				) {
					assistanceService
						.cancelMessageStream({
							conversationId,
							messageId: trackingAttributes.messageId,
						})
						.catch(() => {
							// Log but don't throw - the abort is already happening
							addSentryBreadcrumb({
								message: 'Failed to cancel message stream after abort in sendMessageConfirmedTools',
								data: {
									conversationId,
									messageId: trackingAttributes.messageId,
								},
							});
						});
				}
			};

			try {
				expAndPerfTrackers.start();

				// change the conversation status
				updateCurrentConversation({
					storeAbortController,
					status: CHAT_STORE_STATUS.awaitingResponse,
					lastAgentMessageStatus: 'IN_PROGRESS',
				});

				if (unifiedTasksActions && fg('rovo_chat_enable_tasks')) {
					// Optimistic update goes directly to the tasks store. Explicitly clears
					// current_step / result_summary / error_message so the row resets to a
					// fresh "now running" state.
					const activeTask = unifiedTasksActions.getActiveTaskForConversation(conversationId);
					if (activeTask?.taskId) {
						unifiedTasksActions.setTaskProgressState({
							taskId: activeTask.taskId,
							progressState: 'Running',
							title: activeTask.title ?? '',
							updatedAt: new Date().toISOString(),
							currentStep: null,
							resultSummary: null,
							errorMessage: null,
						});
					}
				}

				// Only add assistant message
				dispatch(
					addAssistantMessage({
						id: tmpAssistantMessageId,
						content: '',
						conversationId,
						agentNamedId: agentId,
					}),
				);
				dispatch(clearFollowUpMessages({ conversationId }));
				trackingAttributes.messageId = tmpAssistantMessageId;
				trackingAttributes.instrumentationId = tmpAssistantMessageId;

				// Assumed the insert is successful and inserts at the last position
				const answerIndex = getMessages(conversationId).length - 1;

				// Behind the `rovo-include-full-context-on-confirmed-tools` experiment, build the
				// same full request context that `sendMessage` sends (creationContext +
				// page/whiteboard/slides/database/dashboard/browser/jira context) so the backend
				// routes tool confirmation through the same code paths as a regular send.
				// Derived to mirror a regular follow-up send: existing conversation
				// (`isFirstPrompt: false`) with content context resolved from the staging-area
				// signal. Failures degrade gracefully to the slim context below.
				let fullConfirmedToolsContext:
					| Partial<Awaited<ReturnType<typeof populateConfirmedToolsContext>>>
					| undefined;
				if (
					FeatureGates.getExperimentValue(
						'rovo-include-full-context-on-confirmed-tools',
						'isEnabled',
						false,
					)
				) {
					try {
						const isFirstPrompt = isEmptyConversation(conversationId);
						const contentContextValue = chatContextStoreActions.getIsStagingArea()
							? 'staging-area'
							: 'global';
						const isEmptyStagingArea =
							contentContextValue === 'staging-area' &&
							hasStagedContent === false &&
							expValEquals('cc-disambiguation-in-cwr', 'isEnabled', true);
						fullConfirmedToolsContext = await populateConfirmedToolsContext(
							chatContextStoreActions,
							isFirstPrompt,
							addCurrentPageAsContextUserPreference,
							contentContextValue,
							productKey,
							sendAnalyticsEvent,
							isEmptyStagingArea,
						);
					} catch (error) {
						addSentryBreadcrumb({
							message:
								'Failed to populate full context for sendMessageConfirmedTools; falling back to slim context',
							data: { conversationId },
						});
					}
				}

				// Call the BE endpoint

				trackingAttributes.initiatedTimestamp = new Date().toISOString();
				const response = await assistanceService.sendMessageConfirmedTools({
					conversationId,
					agentId,
					confirmed_tools,
					controller: abortController,
					optInSSEStream: true,
					storeMessage: storeUserMessages,
					webSearchEnabled,
					deepResearchEnabled,
					thinkDeeperEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					...(taskModeEnabled ? { planModeEnabled: taskModeEnabled } : {}),
					...(includeAIFeatureContext && {
						aiFeatureContext: chatContextStoreActions.getAIFeatureContext(),
					}),
					...(fullConfirmedToolsContext
						? {
								editorContext: fullConfirmedToolsContext.editorContext,
								whiteboardContext: fullConfirmedToolsContext.whiteboardContext,
								slidesContext: fullConfirmedToolsContext.slidesContext,
								databaseContext: fullConfirmedToolsContext.databaseContext,
								dashboardEditorContext: fullConfirmedToolsContext.dashboardEditorContext,
								browserContext: fullConfirmedToolsContext.browserContext,
								additionalContext: fullConfirmedToolsContext.additionalContext,
								// aiFeatureContext is intentionally NOT spread here — it stays owned by the
								// `includeAIFeatureContext` guard above so callers that opt out are respected.
								jiraWorkflowContext: fullConfirmedToolsContext.jiraWorkflowContext,
								dashboardContext: fullConfirmedToolsContext.dashboardContext,
								useCurrentBrowsingContext: fullConfirmedToolsContext.useCurrentBrowsingContext,
								browserContextSanitizer,
							}
						: {}),
					...(resolvedAgentVersion ? { agentVersion: resolvedAgentVersion } : {}),
					optInToDeepResearchV2: true,
					optInThinkDeeperMessages: true,
					optInAnsNow: true,
					optInNewToolTraces: true,
					optInNewTeamworkTraceIcons: fg('rovo_chat_enable_twg_trace_team26'),
					capabilities,
				});
				generator = response.generator;
				responseData = response.responseData;

				// Update action invocation statuses based on the confirmed_tools payload
				if (fg('rovo_chat_enable_tasks')) {
					dispatch(
						updateConfirmedToolsActionStatuses({
							conversationId,
							messageId: confirmed_tools.message_id,
							tools: confirmed_tools.tools,
							status: 'fromResolution',
						}),
					);
				}

				/**
				 * --------
				 * Stream processing START
				 * --------
				 */

				updateCurrentConversation({
					status: CHAT_STORE_STATUS.typingResponse,
				});

				// Research store state shared across stream messages
				const researchState: ResearchStreamState = {
					clientRoundCounter: 0,
					hasAccumulatedAnswerContent: false,
				};

				const processStreamMarkdown = createStreamMarkdownProcessor();

				const isFullContextOnConfirmedToolsEnabled = FeatureGates.getExperimentValue(
					'rovo-include-full-context-on-confirmed-tools',
					'isEnabled',
					false,
				);

				// Tracks whether an AIFC action has started streaming. Mutated in place by
				// processAifcActionStreamMessage.
				const streamActionInfo = { isAction: false };

				const streamToIterate = createChatReconnectableStream({
					generator,
					conversationId,
					abortController,
					assistanceService,
					sendAnalyticsEvent,
					experienceTracker,
					getMessageId: () => agentMessageId,
					canReconnect: () => !!(agentMessageId && isReconnectSupported),
					updateCurrentConversation,
					getIsChatMounted,
					analyticsAttributes: {
						isDeepResearch: deepResearchEnabled,
						thinkDeeperEnabled,
						fastModeEnabled,
					},
					onBeforeFirstReconnect: () => {
						if (agentMessageId) {
							const chat = getMessages(conversationId);
							const alreadySwapped = findStoreMessageIndex(chat, agentMessageId) >= 0;

							if (!alreadySwapped) {
								// Swap the tmp message ID → real agent message ID in the
								// message store so addAnswerPart/replaceAnswerData ID
								// validation doesn't throw on the replayed stream chunks.
								setMessages(
									conversationId,
									updateStoreAssistantMessageId({
										chat,
										oldId: tmpAssistantMessageId,
										newId: agentMessageId,
									}),
								);
								updateResearchMessageId({
									messageId: tmpAssistantMessageId,
									newMessageId: agentMessageId,
								});
							}

							// Reassign so subsequent processResearchStreamMessage calls
							// and addAnswerPart/replaceAnswerData use the real agent
							// message ID as the key.
							tmpAssistantMessageId = agentMessageId;
						}
					},
				});

				for await (const streamObj of streamToIterate) {
					const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;

					/**
					 * Research store processing — handles think-deeper traces, implicit round
					 * detection, and research plan updates for all message types.
					 */
					processResearchStreamMessage({
						streamMessage,
						tmpAssistantMessageId,
						conversationId,
						deepResearchEnabled,
						getMessages,
						setMessages,
						processStreamResearch,
						addImplicitResearchPlan,
						resetHasReceivedAnswerPart,
						researchState,
					});

					/**
					 * Conversation channel data processing (usually the first stream part)
					 */
					if (isConversationChannelDataMessage(streamMessage)) {
						agentMessageId = streamMessage.assistant_message_id;
						trackingAttributes.instrumentationId = agentMessageId;
						trackingAttributes.messageId = agentMessageId;
						analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
						hasLoggedInitiatedEvent = true;
						// Update the research store with the real message ID once we have it
						updateResearchMessageId({
							messageId: tmpAssistantMessageId,
							newMessageId: agentMessageId,
						});
					}

					/**
					 * Reconnect support detection
					 */
					if (isStreamReconnectSupportPart(streamMessage)) {
						isReconnectSupported = true;
					}

					/**
					 * Trace processing
					 */
					if (isStreamTrace(streamMessage)) {
						if (!trackingAttributes.requestId) {
							trackingAttributes.requestId = streamMessage.metadata?.request_id;
						}

						updateCurrentConversation({
							traces: dedupeTraceMessagesWithLatest([
								...(getState().conversations[conversationId]?.traces ?? []),
								getTraceFromStreamMessage(streamMessage),
							]),
						});
					}

					/**
					 * AIFC action processing — streams generated content into the page via the
					 * editor bridge and emits AIFC experiences. Shared with the send-message
					 * flow. Gated behind the confirmed-tools full-context experiment.
					 */
					if (isFullContextOnConfirmedToolsEnabled) {
						processAifcActionStreamMessage({
							streamMessage,
							conversationId,
							tmpAssistantMessageId,
							traceId: responseData?.traceId,
							productKey,
							deepResearchEnabled,
							storeUserMessages,
							streamActionInfo,
							trackingAttributes: {
								requestId: trackingAttributes.requestId,
								instrumentationId: trackingAttributes.instrumentationId,
								forcedContentType: undefined,
							},
							experienceTracker,
							chatContextStoreActions,
							getMessages,
							setMessages,
							sendAnalyticsEvent,
							getIsChatMounted,
							processMessageStreamAction,
							processMessageStreamComplete,
							processMessageStreamActionClassification,
							promptStreamHook: undefined,
							openChat: undefined,
							messageSources,
							creationContextParams: undefined,
							agentId,
							agentName: undefined,
							agentIdentityAccountId: null,
							stopInteractionMetricsTimeToFirstActionPart: () => {},
						});
					}

					/**
					 * Answer part processing
					 */
					if (isStreamAnswerPart(streamMessage)) {
						expAndPerfTrackers.firstAnswerPartReceived();
						clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

						let processedStreamMessage = streamMessage;

						const processedStreamResult = processStreamMarkdown(processedStreamMessage);
						processedStreamMessage = processedStreamResult.processedStreamMessage;

						if (processedStreamResult.markdownData) {
							setMessages(
								conversationId,
								addAnswerPartWithMarkdownData({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									answerPart: processedStreamMessage,
									markdownData: processedStreamResult.markdownData,
									useIndex: answerIndex,
								}),
							);
						} else {
							setMessages(
								conversationId,
								addAnswerPart({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									answerPart: processedStreamMessage,
									useIndex: answerIndex,
								}),
							);
						}
					}

					/**
					 * Final response processing
					 */
					if (isStreamResponse(streamMessage)) {
						trackingAttributes.interactionModeExecuted =
							streamMessage.message?.message?.message_metadata?.interaction_mode_executed;
						hasReceivedFinalResponse = true;

						const humanMessageId = streamMessage?.message?.human_message?.id.toString();
						const finalAgentMessageId = streamMessage?.message?.message?.id.toString();

						clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

						const { processedStreamMessage: processedFinalMessage, markdownData } =
							processStreamResponseMarkdown({
								streamMessage,
								messageId: finalAgentMessageId,
								conversationId,
								sendAnalyticsEvent,
							});

						setMessages(
							conversationId,
							replaceAnswerData({
								chat: getMessages(conversationId),
								answerId: tmpAssistantMessageId,
								content: processedFinalMessage,
								useIndex: answerIndex,
								humanMessageId,
								markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
								onMessageIdChanged: isFullContextOnConfirmedToolsEnabled
									? onTempMessageIdReplaced
									: undefined,
							}),
						);

						// Update the research store key so rounds persist after the message ID changes
						if (finalAgentMessageId && finalAgentMessageId !== tmpAssistantMessageId) {
							updateResearchMessageId({
								messageId: tmpAssistantMessageId,
								newMessageId: finalAgentMessageId,
							});
						}

						trackingAttributes.humanMessageId = humanMessageId;
						trackingAttributes.scenarioId = streamMessage.message.message?.scenario?.id;
						if (!hasLoggedInitiatedEvent) {
							trackingAttributes.messageId = finalAgentMessageId;
							trackingAttributes.instrumentationId = finalAgentMessageId;
						}
					}

					/**
					 * Follow up processing
					 */
					if (isFollowUpMessage(streamMessage)) {
						const messages = getMessages(conversationId);

						clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
						setMessages(conversationId, [
							...messages,
							{
								type: 'FOLLOW_UP',
								content: streamMessage,
								id: randomIntFromInterval(0, 1000000),
								agentMessageId: getLastAgentMessageID(messages),
								humanMessageId: trackingAttributes.humanMessageId,
							},
						]);
					}

					/**
					 * Task status processing
					 */
					if (isStreamTaskStatus(streamMessage)) {
						if (unifiedTasksActions && fg('rovo_chat_enable_tasks')) {
							handleStreamTaskStatus({
								conversationId,
								taskMessage: streamMessage.message,
								messageId: trackingAttributes.messageId,
								target: buildUnifiedStreamTaskTarget(unifiedTasksActions),
							});
						}
					}

					/**
					 * Todo snapshot processing
					 */
					if (isStreamTodoSnapshot(streamMessage)) {
						if (unifiedTasksActions && fg('rovo_chat_enable_tasks')) {
							unifiedTasksActions.setTaskTodoSnapshot({
								conversationId,
								todoSnapshot: { todos: streamMessage.message.todos },
							});
						}
					}

					/**
					 * Error processing
					 */
					if (isStreamError(streamMessage)) {
						const { message, metadata, millisOffset, type } = streamMessage;

						// AUP violations are still considered successful
						if (isStreamAUPError(streamMessage)) {
							expAndPerfTrackers.aupError();
						}

						throw new StreamErrorException(message?.content || 'Unknown', {
							detail: {
								message,
								metadata,
								millisOffset,
								type,
							},
							traceId: response.responseData?.traceId,
						});
					}
				}
			} catch (error) {
				const isAgentDeactivated =
					error instanceof ApiError &&
					error.errorAttributes.errorCode?.includes(
						ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED,
					);
				const isNoAgentPermission =
					error instanceof ApiError &&
					error.errorAttributes.errorCode?.includes(
						ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN,
					) &&
					fg('convo-ai-enable-agents-use-permission');

				if (isAgentDeactivated || isNoAgentPermission) {
					expAndPerfTrackers.agentDeactivated({ error });

					updateCurrentConversation({
						status: CHAT_STORE_STATUS.deactivatedAgentError,
						traces: [],
						errorCode: fg('convo-ai-enable-agents-use-permission')
							? error.errorAttributes.errorCode
							: undefined,
					});

					// Mark all confirmed tool actions as errored
					if (fg('rovo_chat_enable_tasks')) {
						dispatch(
							updateConfirmedToolsActionStatuses({
								conversationId,
								messageId: confirmed_tools.message_id,
								tools: confirmed_tools.tools,
								status: 'errored',
							}),
						);
					}

					!hasLoggedInitiatedEvent &&
						analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
					analyticsTrackers.error();
					return;
				}

				if (error instanceof Error && error.name === 'AbortError') {
					handleAbort();
					return;
				}

				// Mark all confirmed tool actions as errored
				if (fg('rovo_chat_enable_tasks')) {
					dispatch(
						updateConfirmedToolsActionStatuses({
							conversationId,
							messageId: confirmed_tools.message_id,
							tools: confirmed_tools.tools,
							status: 'errored',
						}),
					);
				}

				expAndPerfTrackers.fail({
					error: error instanceof Error ? error : new Error(String(error)),
				});
				markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
				!hasLoggedInitiatedEvent &&
					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.error();
				throw error;
			} finally {
				updateCurrentConversation(clearStatus({}));
				setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));
			}

			/**
			 * --------
			 * Stream processing END
			 * --------
			 */

			// Handle programmatic abort — the generator may exit cleanly without throwing AbortError
			// when the abort controller is signalled. Check the signal to detect this case.
			if (abortController.signal.aborted) {
				handleAbort();
				return;
			}

			// if stream finishes without FINAL_RESPONSE, BE is hanging
			if (!hasReceivedFinalResponse) {
				const error = new NoFinalResponseError(
					'The response took too long and timed out. Try refreshing the page.',
					{
						traceId: responseData?.traceId,
					},
				);
				experienceTracker.fail({
					name: confirmedToolsExperienceName,
					error,
				});
				markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
				!hasLoggedInitiatedEvent &&
					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.error();
				throw error;
			}

			!hasLoggedInitiatedEvent &&
				analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
			analyticsTrackers.viewed();
			expAndPerfTrackers.succeed();
			if (fg('rovo_chat_chat_history_refresh')) {
				//If user is viewing a different conversation or not viewing ConversationAssitant, optimistically set conversation to unread
				if (getState().currentConversationId !== conversationId) {
					dispatch(
						setConversationData({
							conversationId,
							data: { readStatus: false },
						}),
					);
				}
			}
		},
);

const removeMessage = createSafeAction(
	StoreActionLoggableName.removeMessage,
	({
		messageId,
		conversationId: conversationIdParam,
	}: {
		messageId: number;
		conversationId?: string;
	}): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const filteredMessages = messages.filter((message) => {
				if (isStoreError(message) || isStoreFollowUp(message)) {
					return message.id !== messageId;
				} else if (isStoreHuman(message)) {
					return message.content.id !== String(messageId);
				}

				// Catch-all to keep any other message type
				else {
					return true;
				}
			});

			setMessages(conversationId, filteredMessages);
		},
);

/**
 * Hides the last HUMAN message + everything after it, and returns the removed
 * slice so the caller can pass it as `editRollback.restoreMessages` to
 * `sendMessage` for automatic rollback on terminal failure.
 */
const truncateFromLastHumanMessage = createSafeAction<
	(args: { conversationId?: string }) => ChatStoreAction<StoreChatItem[]>,
	StoreChatItem[]
>(
	StoreActionLoggableName.truncateFromLastHumanMessage,
	({
		conversationId: conversationIdParam,
	}: {
		conversationId?: string;
	}): ChatStoreAction<StoreChatItem[]> =>
		({ getState }, { getMessages, setMessages }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return [];
			}

			const messages = getMessages(conversationId);
			const lastHumanIdx = findLastIndex(messages, isStoreHuman);
			if (lastHumanIdx < 0) {
				return [];
			}

			const tail = messages.slice(lastHumanIdx);
			setMessages(conversationId, messages.slice(0, lastHumanIdx));
			return tail;
		},
);

/**
 * Reconnects to an in-progress message stream when loading a conversation
 * where the last message has async_status 'IN_PROGRESS'
 */
const reconnectToInProgressStream = createSafeAction(
	StoreActionLoggableName.reconnectToInProgressStream,
	({
		conversationId,
		messageId,
		lastEventId,
		isDeepResearch,
		abortCurrentRequest,
		answerIndex,
		onComplete,
	}: ReconnectToInProgressStreamParams): ChatStoreAction =>
		reconnectToInProgressStreamProvider({
			conversationId,
			messageId,
			lastEventId,
			isDeepResearch,
			abortCurrentRequest,
			answerIndex,
			onComplete,
			setConversationData,
		}),
	{
		experienceName: () => ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
	},
);

/**
 * Processes raw chat messages from the API into StoreChatItem[] with boundary content extraction.
 * Shared by fetchConversation and warmConversationCache.
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const processConversationMessages = ({
	latestMessages,
	conversationId,
	sendAnalyticsEvent,
	shouldPreprocessDetails = false,
}: {
	latestMessages: Awaited<ReturnType<ChatStoreContainerProps['assistanceService']['getChat']>>;
	conversationId: string;
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	/**
	 * When true, rewrite HTML `<details><summary>` blocks into the Rovo-native
	 * `▞…▚` boundary before the expand-boundary extractor runs. Mirrors the
	 * streaming/final-response paths so history-loaded messages render the same
	 * way. Callers gate this on `shouldPreprocessDetailsForConversation(...)`.
	 */
	shouldPreprocessDetails?: boolean;
}): StoreChatItem[] => {
	const messages: StoreChatItem[] = [];

	for (const message of latestMessages) {
		if (isHumanMessage(message)) {
			// Human messages should only have application/json+a2ui if they have sent an action
			// we skip this message as there is nothing to render but this is persisted for flexibility
			// there should be no cases where a human renders A2UI from their messages
			if (
				typeof message.content === 'object' &&
				message.content !== null &&
				'data_parts_mime_type' in message.content &&
				message.content.data_parts_mime_type === 'application/json+a2ui' &&
				fg('jira_ai_rendering_a2ui')
			) {
				continue;
			}
			messages.push(createStoreHumanWithContent(message));
		} else if (isStreamResponse(message)) {
			// Process boundary content for historical assistant messages
			let processedMessage = message;
			const markdownData: Record<string, string> = {};

			// Rewrite HTML `<details><summary>` blocks into the Rovo-native `▞…▚`
			// boundary before the existing extractor runs so historical messages
			// render identically to their streamed form. Must happen BEFORE the
			// expand extractor below (which consumes `▞…▚`).
			if (shouldPreprocessDetails) {
				processedMessage = preprocessStreamResponseDetails(processedMessage);
			}

			// Apply the same boundary content extraction logic as streaming messages
			if (fg('rovo_enable_data_sculptor')) {
				const sculptorMsg = extractMarkdownContent({
					streamMessage: processedMessage,
					boundaryStart: '[^sculptor:start^]',
					boundaryEnd: '[^sculptor:end^]',
					replacementContent: '[^sculptor^]',
				});
				processedMessage = sculptorMsg.processedStreamMessage as StreamResponse;
				sculptorMsg.boundaryContents.forEach((content, index) => {
					markdownData[`sculptor-${index}`] = content;
				});
			}

			const expandOpenTag = '▞';
			const expandCloseTag = '▚';
			const expandPlaceholder = '[^expand^]';

			const expandMsg = extractMarkdownContent({
				streamMessage: processedMessage,
				boundaryStart: expandOpenTag,
				boundaryEnd: expandCloseTag,
				replacementContent: expandPlaceholder,
			});
			processedMessage = expandMsg.processedStreamMessage as StreamResponse;
			expandMsg.boundaryContents.forEach((content, index) => {
				markdownData[`expand-${index}`] = content;
			});

			const chartPlaceholder = '[^chart^]';
			// When processing conversation history, process both unicode and non-unicode tags
			// to handle previous conversations that used the non-unicode tag
			// Once di_chat_chart_tag_unicode is rolled out, chartMsg can be removed after 30 days
			let chartIndex = 0;

			const chartMsg = extractMarkdownContent({
				streamMessage: processedMessage,
				boundaryStart: '[^chart:start^]',
				boundaryEnd: '[^chart:end^]',
				replacementContent: chartPlaceholder,
			});
			processedMessage = chartMsg.processedStreamMessage as StreamResponse;
			chartMsg.boundaryContents.forEach((content) => {
				markdownData[`chart-${chartIndex}`] = content;
				chartIndex++;
			});

			if (chartMsg?.boundaryContents && chartMsg.boundaryContents.length > 0) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'extracted',
					actionSubject: 'vizPluginChartNonUnicode',
					attributes: {
						messageId: message?.message?.message?.id.toString(),
						conversationId,
						chartCount: chartMsg.boundaryContents.length,
						timestamp: new Date().toISOString(),
					},
				});
			}

			const unicodeChartMsg = extractMarkdownContent({
				streamMessage: processedMessage,
				boundaryStart: '◴',
				boundaryEnd: '◶',
				replacementContent: chartPlaceholder,
			});
			processedMessage = unicodeChartMsg.processedStreamMessage as StreamResponse;
			unicodeChartMsg.boundaryContents.forEach((content) => {
				markdownData[`chart-${chartIndex}`] = content;
				chartIndex++;
			});

			if (fg('rovo_chat_enable_inline_lumina')) {
				const luminaMsg = extractMarkdownContent({
					streamMessage: processedMessage,
					boundaryStart: '◐',
					boundaryEnd: '◑',
					replacementContent: '\n\n[^lumina^]\n\n',
				});
				processedMessage = luminaMsg.processedStreamMessage as StreamResponse;
				luminaMsg.boundaryContents.forEach((content, index) => {
					markdownData[`lumina-${index}`] = content;
				});
			}

			// Extract actionable follow-ups so history-loaded messages render the
			// same as their streamed form. Without this, the raw `◈<followup>…`
			// marker renders literally when reopening a past conversation. No-ops
			// when the experiment is in the `control` cohort.
			processedMessage = extractActionableFollowUpsMarkdownData({
				streamMessage: processedMessage,
				markdownData,
			});

			// When the backend returns structured content (A2UI messages), normalize
			// it back to a plain text string and extract data_parts for A2UI rendering.
			let extractedA2UIMessages: A2UIMessage[] | undefined;
			if (fg('jira_ai_rendering_a2ui')) {
				const a2uiResult = extractA2UIFromStructuredContent(
					processedMessage.message?.message?.content,
				);
				if (a2uiResult) {
					processedMessage.message.message.content = a2uiResult.textContent;
					extractedA2UIMessages = a2uiResult.a2uiMessages;
				}
			}

			// Create StoreAssistant with processed boundary data
			const storeAssistant = createStoreAssistantWithContent(processedMessage);
			if (Object.keys(markdownData).length > 0) {
				storeAssistant.markdownData = markdownData;
			}

			// Mark message as historical since it's loaded from conversation history
			storeAssistant.isHistorical = true;

			if (extractedA2UIMessages) {
				storeAssistant.agenticUiMessages = extractedA2UIMessages;
			}

			messages.push(storeAssistant);
		}
	}

	return messages;
};

/**
 * Extracts agentVersion from the first assistant message's agent_author field.
 * Only loops until the first assistant message is found.
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const extractAgentVersionFromMessages = (
	latestMessages: Awaited<ReturnType<ChatStoreContainerProps['assistanceService']['getChat']>>,
): AgentVersion | undefined => {
	if (!fg('rovo_agent_versioning_enabled')) {
		return undefined;
	}

	for (const message of latestMessages) {
		if (isStreamResponse(message)) {
			const agentAuthor = message.message?.message?.agent_author;
			if (agentAuthor?.versionId || agentAuthor?.versionType) {
				return {
					versionId: agentAuthor.versionId,
					versionType: agentAuthor.versionType,
					versionNumber: agentAuthor.versionNumber,
				};
			}
			return undefined;
		}
	}

	return undefined;
};

const restoreTaskModeForConversation =
	({
		conversationId,
		messages,
	}: {
		conversationId: string;
		messages: StoreChatItem[];
	}): ChatStoreAction =>
	(_, { setReasoningMode, unifiedTasksActions }) => {
		if (!fg('rovo_chat_enable_tasks') || !unifiedTasksActions) {
			return;
		}

		const activeTask = unifiedTasksActions.getActiveTaskForConversation(conversationId);
		const lastAgentMsgId = getLastAgentMessageID(messages);
		if (
			activeTask &&
			!isTerminalTaskState(activeTask.progressState) &&
			lastAgentMsgId &&
			activeTask.currentMessageId === lastAgentMsgId
		) {
			setReasoningMode(conversationId, 'task');
		}
	};

const isForbiddenConversationError = (error: unknown): error is ApiError =>
	error instanceof ApiError && error.errorAttributes?.statusCode === 403;

const handleForbiddenConversation = ({
	conversationId,
	dispatch,
	sendAnalyticsEvent,
	showFlag,
}: {
	conversationId: string;
	dispatch: StoreActionApi<ChatStorePrivateState>['dispatch'];
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	showFlag?: ChatStoreContainerProps['showFlag'];
}): void => {
	const rovoParams = getRovoParams();
	const isConversationIdInRovoParams = !!rovoParams?.conversationId;

	sendAnalyticsEvent({
		eventType: 'track',
		action: 'forbidden',
		actionSubject: 'rovoChatFetchConversation',
		attributes: {
			isConversationIdInRovoParams,
		},
	});

	dispatch(removeConversationFromStore({ conversationId }));
	dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));

	if (isConversationIdInRovoParams) {
		updatePageRovoParams({
			conversationId: undefined,
		});
	}

	showFlag?.(StoreErrorFlagId.CONVERSATION_FORBIDDEN);
};

type FetchConversationArgs = {
	conversationId?: string;
	messageIdSelectedForPreview?: string | null;
	/**
	 * Mount/remount callers can opt into keeping an already-hydrated idle
	 * conversation intact. Default fetch callers still refresh as before.
	 */
	skipIfConversationAlreadyLoaded?: boolean;
};

const fetchConversation = createSafeAction(
	StoreActionLoggableName.fetchConversation,
	({
		conversationId: conversationIdParam,
		messageIdSelectedForPreview,
		skipIfConversationAlreadyLoaded = false,
	}: FetchConversationArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				getMessages,
				setMessages,
				showFlag,
				setReasoningMode,
				capabilities,
			},
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const conversation = getState().conversations[conversationId];

			if (conversation?.status !== CHAT_STORE_STATUS.idle || isEmptyConversation(conversationId)) {
				return;
			}

			if (conversation.skipNextFetch) {
				dispatch(
					setConversationData({
						conversationId,
						data: { skipNextFetch: false },
					}),
				);
				return;
			}

			if (
				skipIfConversationAlreadyLoaded &&
				!messageIdSelectedForPreview &&
				conversation.lastAgentMessageStatus !== 'FAILED' &&
				(getMessages(conversationId).length > 0 || conversation.messages.length > 0)
			) {
				return;
			}

			experienceTracker.start({
				name: ExperienceName.FETCH_CHAT,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			if (conversation.storeAbortController) {
				await conversation.storeAbortController?.abort();
			}

			dispatch(
				resetConversationForLoading({
					conversationId,
					traces:
						messageIdSelectedForPreview && fg('aifc_create_enabled')
							? []
							: [{ messageContent: 'Reloading chat', status: 'loading' }],
					sourceAgentId: getSourceAgentId(
						getMessages(conversationId),
						getConversation(getState(), conversationId),
					),
				}),
			);

			let serviceChat: Awaited<ReturnType<typeof assistanceService.getChat>>;
			try {
				serviceChat = await assistanceService.getChat(conversationId, capabilities);
			} catch (e) {
				if (isChannelNotFoundError(e)) {
					//We succeed this event because in sendMessage, we create a new channel if we receive a 404 response
					// Users will not see this error, so exclude it from Sentry
					//This is a fix due to an incompatible BE API change
					sendAnalyticsEvent({
						eventType: 'operational',
						action: 'notFound',
						actionSubject: 'rovoChatFetchConversation',
					});

					experienceTracker.succeed({
						name: ExperienceName.FETCH_CHAT,
					});

					dispatch(
						createConversationOptimistically({
							sourceAgentId: getSourceAgentId(
								getMessages(conversationId),
								getConversation(getState(), conversationId),
							),
						}),
					);
					dispatch(removeConversationFromStore({ conversationId }));

					return;
				}

				if (isForbiddenConversationError(e) && fg('rovo_chat_improved_conversation_403_handling')) {
					experienceTracker.fail({
						name: ExperienceName.FETCH_CHAT,
						error: e,
					});

					handleForbiddenConversation({
						conversationId,
						dispatch,
						sendAnalyticsEvent,
						showFlag,
					});

					return;
				}

				throw e;
			}

			// There's a bug currently in the playwright pubsub test where when adding in a new
			// async function to sendMessageStream it causes the message to be dropped
			const status = getState().conversations[conversationId]?.status;

			if (status !== CHAT_STORE_STATUS.loadingChat) {
				return;
			}

			const latestMessages = serviceChat;

			const messages = processConversationMessages({
				latestMessages,
				conversationId,
				sendAnalyticsEvent,
				shouldPreprocessDetails: shouldPreprocessDetailsForConversation(getState(), conversationId),
			});

			const fetchedAgentVersion = extractAgentVersionFromMessages(latestMessages);

			dispatch(
				setConversationData({
					conversationId,
					data: {
						...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
						sourceAgentId: getSourceAgentId(
							getMessages(conversationId),
							getConversation(getState(), conversationId),
						),
						...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
					},
				}),
			);

			// Check if the last message has async_status 'IN_PROGRESS' and reconnect to the stream
			const lastMessage = latestMessages.at(-1);
			const isDeepResearch = (lastMessage as StreamResponse)?.message?.message?.message_metadata
				?.deep_research;

			if (isInProgressStreamResponse(lastMessage)) {
				const messageId = lastMessage.message.message.id;
				// Clear the "Message is in progress" content of the last agent message
				const updatedMessages = clearStoreAssistantMessageContent(messages, messageId);
				setMessages(conversationId, updatedMessages);

				dispatch(
					reconnectToInProgressStream({
						conversationId,
						messageId,
						isDeepResearch,
					}),
				);
			} else {
				setMessages(conversationId, messages);
			}

			dispatch(restoreTaskModeForConversation({ conversationId, messages }));

			experienceTracker.succeed({
				name: ExperienceName.FETCH_CHAT,
			});
		},
	{
		experienceName: () => ExperienceName.FETCH_CHAT,
	},
);

// Abort requests for a specific conversation, or ALL conversations if no conversationId is passed
const abortRequests = createSafeAction(
	StoreActionLoggableName.abortRequests,
	({
		conversationId,
		thinkDeeperEnabled,
		fastModeEnabled,
		taskModeEnabled,
		deepResearchEnabled,
	}: {
		conversationId?: string;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		fastModeEnabled?: boolean;
		taskModeEnabled?: boolean;
	} = {}): ChatStoreAction =>
		(
			{ getState, dispatch },
			{
				experienceTracker,
				setReasoningMode,
				isReconnecting,
				unifiedTasksActions,
				getMessages,
				setMessages,
			},
		) => {
			const conversations = getState().conversations;

			const abortConversationStream = (conversation: Conversation) => {
				if (conversation?.storeAbortController) {
					experienceTracker.start({
						name: ExperienceName.ABORT_AGENT_STREAM,
						id: conversation?.id,
						attributes: {
							conversationId: conversation?.id,
							isReconnecting: isReconnecting(conversation?.id ?? ''),
						},
					});

					conversation.storeAbortController.abort();

					if (
						fg('cc-cwr-fix-finalizing-cancelled-request') &&
						conversation.id &&
						!isReconnecting(conversation.id)
					) {
						const currentMessages = getMessages(conversation.id);
						const nextMessages = markInFlightActionsAsCancelled(currentMessages);
						if (nextMessages !== currentMessages) {
							setMessages(conversation.id, nextMessages);
						}
					}

					experienceTracker.abort({
						name: getSendMessageExperienceName({
							deepResearchEnabled,
							thinkDeeperEnabled,
							taskModeEnabled,
							fastModeEnabled,
						}),
						reason: 'Aborted',
						attributes: {
							isProgrammaticAbort: true,
						},
					});

					experienceTracker.succeed({
						name: ExperienceName.ABORT_AGENT_STREAM,
					});

					if (
						taskModeEnabled &&
						conversation?.id &&
						unifiedTasksActions &&
						fg('rovo_chat_enable_tasks')
					) {
						unifiedTasksActions.setTaskProgressState({
							conversationId: conversation.id,
							progressState: 'Cancelled',
							updatedAt: new Date().toISOString(),
						});
					}
				}
			};

			if (conversationId) {
				abortConversationStream(conversations[conversationId]);
			} else {
				Object.values(conversations).forEach(abortConversationStream);
			}
		},
	{
		experienceName: () => ExperienceName.ABORT_AGENT_STREAM,
	},
);

/**
 * Triggers Answer Now functionality to fast-forward extended thinking.
 * This action performs an optimistic update:
 * 1. Sets answerNowStatus to SWITCHING immediately
 * 2. Calls the Answer Now API endpoint
 * 3. Sets status to ERRORED if API call fails (hides button, prevents retry)
 *
 * Note: Research steps and message content are NOT cleared here - they continue
 * to be displayed during SWITCHING. Clearing happens when FAST_MODE_ENABLED is received.
 */
const answerNow = createSafeAction(
	StoreActionLoggableName.answerNow,
	({
		conversationId: conversationIdParam,
		tmpMessageId,
		remoteMessageId,
	}: {
		conversationId?: string;
		tmpMessageId: string; // For store operations (finding/updating the message)
		remoteMessageId: string; // For the API call
	}): ChatStoreAction =>
		async (
			{ getState, setState },
			{ assistanceService, experienceTracker, getMessages, setMessages },
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			// Helper to update conversation traces
			const updateConversationTrace = (traceMessage: string) => {
				const state = getState();
				const conversation = state.conversations[conversationId];
				if (conversation) {
					setState({
						conversations: {
							...state.conversations,
							[conversationId]: {
								...conversation,
								traces: [{ messageContent: traceMessage }],
							},
						},
					});
				}
			};

			// Verify the message exists and has answerNowStatus === AVAILABLE
			// Also prevents duplicate requests if status is already SWITCHING
			const messages = getMessages(conversationId);
			const targetMessage = messages.find(
				(msg) => isStoreAssistant(msg) && msg.content?.message?.message?.id === tmpMessageId,
			);

			if (
				!targetMessage ||
				!isStoreAssistant(targetMessage) ||
				!isAnswerNowAvailable(targetMessage.answerNowStatus)
			) {
				return;
			}

			// Optimistic update: Set SWITCHING status first and update trace message
			const updatedMessages = updateStoreAssistantMessageAnswerNowStatus(
				getMessages(conversationId),
				tmpMessageId,
				{ type: AnswerNowStatusType.SWITCHING },
			);
			setMessages(conversationId, updatedMessages);
			updateConversationTrace('Generating answer now!');

			// Start experience tracking
			experienceTracker.start({
				name: ExperienceName.ANSWER_NOW,
				id: conversationId,
				attributes: {
					conversationId,
					remoteMessageId,
					tmpMessageId,
				},
			});

			try {
				// Call the Answer Now API with the remote (BE-provided) message ID
				await assistanceService.answerNow(conversationId, remoteMessageId);

				// Only set SWITCHING_REQUESTED if status is still SWITCHING (race condition guard)
				const currentMessages = getMessages(conversationId);
				const currentMessage = currentMessages.find(
					(msg) => isStoreAssistant(msg) && msg.content?.message?.message?.id === tmpMessageId,
				);
				if (
					currentMessage &&
					isStoreAssistant(currentMessage) &&
					currentMessage.answerNowStatus?.type === AnswerNowStatusType.SWITCHING
				) {
					const successMessages = updateStoreAssistantMessageAnswerNowStatus(
						currentMessages,
						tmpMessageId,
						{ type: AnswerNowStatusType.SWITCHING_REQUESTED },
					);
					setMessages(conversationId, successMessages);
				}

				// Track success
				experienceTracker.succeed({
					name: ExperienceName.ANSWER_NOW,
				});
			} catch (error) {
				// Only set ERRORED if status is still SWITCHING (race condition guard)
				const currentMessages = getMessages(conversationId);
				const currentMessage = currentMessages.find(
					(msg) => isStoreAssistant(msg) && msg.content?.message?.message?.id === tmpMessageId,
				);
				if (
					currentMessage &&
					isStoreAssistant(currentMessage) &&
					currentMessage.answerNowStatus?.type === AnswerNowStatusType.SWITCHING
				) {
					const erroredMessages = updateStoreAssistantMessageAnswerNowStatus(
						currentMessages,
						tmpMessageId,
						{ type: AnswerNowStatusType.ERRORED },
					);
					setMessages(conversationId, erroredMessages);
					updateConversationTrace('Generating faster answer failed, continuing with current mode');
				}

				// Track failure
				experienceTracker.fail({
					name: ExperienceName.ANSWER_NOW,
					error: error instanceof Error ? error : new Error(String(error)),
				});

				throw error;
			}
		},
);

const addErrorMessage = createSafeAction(
	StoreActionLoggableName.addErrorMessage,
	({
		error,
		conversationId,
		errorPrefix,
	}: {
		error: unknown;
		conversationId?: string;
		errorPrefix?: string;
	}): ChatStoreAction =>
		({ dispatch }) => {
			dispatch(
				addStoreChatItem({
					message: createStoreError(error, errorPrefix),
					conversationId,
				}),
			);
		},
);

export type FetchConversationChannelsDataArgs = {
	isInitialLoad?: boolean;
	reconcileFirstPage?: boolean;
	onError?: OnErrorCallback<FetchConversationChannelsDataArgs>;
};

const getProcessedConversationChannels = ({
	channelsFromAPI,
	conversations,
	conversationsDeletedDuringCall = new Set<string>(),
}: {
	channelsFromAPI: ConversationChannelsResponse['items'];
	conversations: ChatStorePrivateState['conversations'];
	conversationsDeletedDuringCall?: Set<string>;
}): Record<string, Conversation> => {
	const localConversationByBackendId = new Map<string, Conversation>(
		Object.values(conversations)
			.filter((c): c is Conversation & { backendConversationId: string } =>
				Boolean(c.backendConversationId),
			)
			.map((c) => [c.backendConversationId, c]),
	);

	return channelsFromAPI
		.filter((channel) => !conversationsDeletedDuringCall.has(channel.id))
		.reduce(
			(acc, channel) => {
				const localConversation =
					conversations[channel.id] ?? localConversationByBackendId.get(channel.id);
				acc[channel.id] = mergeConversationWithNetworkPriority(channel, localConversation);

				return acc;
			},
			{} as Record<string, Conversation>,
		);
};

const mergeConversationChannelTasks = (
	channelsFromAPI: ConversationChannelsResponse['items'],
	unifiedTasksActions?: ChatStoreContainerProps['unifiedTasksActions'],
) => {
	if (!unifiedTasksActions) {
		return;
	}

	const allChannelTasks = channelsFromAPI.flatMap((channel) => channel.tasks ?? []);
	if (allChannelTasks.length > 0) {
		unifiedTasksActions.mergeConversationChannelTasks(allChannelTasks);
	}
};

const fetchConversationChannelsData = createSafeAction(
	StoreActionLoggableName.fetchConversationChannelsData,
	({
		isInitialLoad = false,
		reconcileFirstPage = false,
	}: FetchConversationChannelsDataArgs = {}): ChatStoreAction =>
		async (
			{ setState, getState, dispatch },
			{ assistanceService, experienceTracker, unifiedTasksActions },
		) => {
			const shouldUsePagination = isConversationListPaginationEnabled();

			if (
				shouldUsePagination &&
				reconcileFirstPage &&
				!getState().conversationPagination.isConversationListMounted
			) {
				return;
			}

			// A concurrent load-more may be in flight; skip the first-page reconciliation
			// rather than stomping its cursor/hasMore metadata. Best-effort: the next
			// visibility event will reconcile correctly.
			if (
				shouldUsePagination &&
				reconcileFirstPage &&
				getState().conversationPagination.isLoadingMore
			) {
				return;
			}

			if (isInitialLoad) {
				experienceTracker.start({
					id: ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD,
					name: ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD,
				});
			} else {
				experienceTracker.start({
					id: ExperienceName.GET_CONVERSATION_CHANNELS,
					name: ExperienceName.GET_CONVERSATION_CHANNELS,
				});
			}

			// Capture conversation IDs before API call to detect changes during the call
			const conversationsBeforeAPICall = new Set(Object.keys(getState().conversations));

			const conversationChannelsResponse = shouldUsePagination
				? await assistanceService.getConversationChannels({ limit: CONVERSATION_LIST_PAGE_SIZE })
				: await assistanceService.getConversationChannels();
			const channelsFromAPI = conversationChannelsResponse.items;
			const { conversations, currentConversationId } = getState();

			const channelIds = new Set(channelsFromAPI.map((channel) => channel.id));

			// Identify conversations deleted during the API call (were there before but aren't now)
			const conversationsDeletedDuringCall = new Set(
				Array.from(conversationsBeforeAPICall).filter(
					(id) => !conversations[id] && !isEmptyConversation(id),
				),
			);

			// Identify conversations created during the API call (exist in state but weren't there before and aren't in API response)
			const conversationsCreatedDuringCall = new Set(
				Object.keys(conversations).filter(
					(id) => !conversationsBeforeAPICall.has(id) && !channelIds.has(id),
				),
			);

			// Preserve all local conversations not yet on the server
			// (they may have just finished processing or be in-flight)
			// e.g. optimistic conversation when URL params are used to open a chat
			const optimisticConversationsToPreserve = new Set(
				Object.keys(conversations).filter((id) => !channelIds.has(id) && !isEmptyConversation(id)),
			);

			const conversationIdsToPreserve = new Set([
				...conversationsCreatedDuringCall,
				...optimisticConversationsToPreserve,
			]);

			const conversationsToPreserve = Object.fromEntries(
				Array.from(conversationIdsToPreserve, (id) => [id, conversations[id]]),
			);

			const processedChannels = getProcessedConversationChannels({
				channelsFromAPI,
				conversations,
				conversationsDeletedDuringCall,
			});

			const allChannelTasks = channelsFromAPI.flatMap((channel) => channel.tasks ?? []);
			mergeConversationChannelTasks(channelsFromAPI, unifiedTasksActions);

			// Hydrate the store with the fetched conversation channels
			// whilst preserving the empty state and and any conversations created during the API call, so drafts are never lost
			// due to rehydration (e.g window refocus) when this is called
			setState({
				conversations: {
					EMPTY_CONVERSATION_ID: conversations[EMPTY_CONVERSATION_ID],
					...processedChannels,
					...conversationsToPreserve,
				},
				isConversationsInitialized: true,
				conversationPagination: {
					...getState().conversationPagination,
					nextCursor: shouldUsePagination ? conversationChannelsResponse.cursor : null,
					hasMore: shouldUsePagination ? conversationChannelsResponse.hasNext : false,
					isLoadingMore: false,
					loadMoreError: null,
				},
			});

			const currentConversationDeleted =
				!isEmptyConversation(currentConversationId) &&
				!channelIds.has(currentConversationId) &&
				!conversationIdsToPreserve.has(currentConversationId);

			if (currentConversationDeleted && !isInitialLoad) {
				dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
			}

			const firstChannelTask = allChannelTasks[0];
			const shouldRestoreTaskMode =
				firstChannelTask !== undefined &&
				!isTerminalTaskState(mapApiTaskStatusToProgressState(firstChannelTask.status));

			if (isInitialLoad && fg('rovo_chat_enable_tasks') && shouldRestoreTaskMode) {
				dispatch(restoreTaskModeIfNeeded());
			}

			experienceTracker.succeed({
				name: isInitialLoad
					? ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD
					: ExperienceName.GET_CONVERSATION_CHANNELS,
			});
		},
	{
		experienceName: (args) =>
			args?.[0]?.isInitialLoad
				? ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD
				: ExperienceName.GET_CONVERSATION_CHANNELS,
		actionErrorHandler: (context) => {
			const { onError, ...args } = context.args?.[0] ?? {};
			return onError ? onError({ ...context, args }) : { proceedToDefaultHandling: true };
		},
	},
);

const loadMoreConversationChannelsData = createSafeAction(
	StoreActionLoggableName.loadMoreConversationChannelsData,
	(): ChatStoreAction =>
		async ({ setState, getState }, { assistanceService, unifiedTasksActions }) => {
			if (!isConversationListPaginationEnabled()) {
				return;
			}

			const { conversationPagination } = getState();
			if (
				conversationPagination.isLoadingMore ||
				!conversationPagination.hasMore ||
				!conversationPagination.nextCursor
			) {
				return;
			}

			setState({
				conversationPagination: {
					...conversationPagination,
					isLoadingMore: true,
					loadMoreError: null,
				},
			});

			try {
				const conversationChannelsResponse = await assistanceService.getConversationChannels({
					limit: CONVERSATION_LIST_PAGE_SIZE,
					cursor: conversationPagination.nextCursor,
				});
				const channelsFromAPI = conversationChannelsResponse.items;
				const { conversations } = getState();
				const processedChannels = getProcessedConversationChannels({
					channelsFromAPI,
					conversations,
				});

				mergeConversationChannelTasks(channelsFromAPI, unifiedTasksActions);

				setState({
					conversations: {
						...conversations,
						...processedChannels,
					},
					conversationPagination: {
						...getState().conversationPagination,
						nextCursor: conversationChannelsResponse.cursor,
						hasMore: conversationChannelsResponse.hasNext,
						isLoadingMore: false,
						loadMoreError: null,
					},
				});
			} catch (error) {
				setState({
					conversationPagination: {
						...getState().conversationPagination,
						isLoadingMore: false,
						loadMoreError: createStoreError(
							error,
							undefined,
							StoreActionLoggableName.loadMoreConversationChannelsData,
						),
					},
				});
			}
		},
);

const setConversationListMounted =
	(isConversationListMounted: boolean): ChatStoreAction =>
	({ setState, getState }) => {
		setState({
			conversationPagination: {
				...getState().conversationPagination,
				isConversationListMounted,
			},
		});
	};

const getTimeoutBeforeExpiration = (): number => {
	return SHORTER_TIMEOUT_BEFORE_NEW_CHAT;
};

/*
 * This action runs on initial load to hydrate this in-memory store (chatStore)
 * with data from either URL parameters or LocalStorage. URL parameters take precedence.
 *
 * Setting the currentConversationId (using setSelectedConversation or createConversationOptimistically)
 * may implicitly trigger fetching of conversation messages.
 *
 * Any change to currentConversationId will trigger a useEffect in ChatHistory,
 * which then executes the `fetchConversation` action.
 */
const hydrateConversationWithExternalParams = createSafeAction(
	StoreActionLoggableName.hydrateConversation,
	({ autoSelectConversation }: { autoSelectConversation: boolean }): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				siteId,
				experienceTracker,
				setChatStatus,
				initChatInitiator,
				insertHighlightActionsEditorPlaceholder,
				setStagingAreaMode,
				setMessageIdSelectedForPreview,
				getStoreUserMessagesPreference,
			},
		) => {
			experienceTracker.start({
				id: ExperienceName.HYDRATE_CONVERSATION,
				name: ExperienceName.HYDRATE_CONVERSATION,
			});

			const rovoParamsUnsanitized = getRovoParams();
			const rovoParams = Object.fromEntries(
				Object.entries(rovoParamsUnsanitized).filter(([, v]) => v !== 'undefined'),
			) as typeof rovoParamsUnsanitized;

			let initWithParams = autoSelectConversation && rovoParams.pathway === 'chat';
			const { currentConversationId } = getState();

			if (rovoParams.stagingAreaOpen && fg('aifc_create_enabled')) {
				setStagingAreaMode('inline', 'rovoChat');
				updatePageRovoParams({ stagingAreaOpen: undefined });
			}
			if (rovoParams.messageIdSelectedForPreview && fg('aifc_create_enabled')) {
				setMessageIdSelectedForPreview(rovoParams.messageIdSelectedForPreview);
				updatePageRovoParams({ messageIdSelectedForPreview: undefined });
			}

			if (rovoParams.triggerOpen) {
				// if the rovo chat was actually open with url param
				initChatInitiator(rovoParams.initiator);
			}

			// Process params as a priority
			if (initWithParams) {
				const localConversation = rovoParams.conversationId
					? getState().conversations[rovoParams.conversationId]
					: undefined;

				if (rovoParams.conversationId && localConversation) {
					dispatch(
						setSelectedConversation({
							conversationId: rovoParams.conversationId,
							sourceAgentId: rovoParams.agentId,
						}),
					);
					// Clear conversationId from URL after consuming it so subsequent chat opens
					// don't reuse it and get stuck in the same conversation.
					updatePageRovoParams({ conversationId: undefined });
				} else {
					dispatch(
						createConversationOptimistically({
							id: rovoParams.conversationId,
							sourceAgentId: rovoParams.agentId,
						}),
					);
				}

				if (rovoParams.prompt) {
					// For cases where the prompt is a json string containing ADF, we need to parse it
					const shouldParse = isValidAdfJsonString(rovoParams.prompt);
					const prompt = shouldParse
						? (JSON.parse(rovoParams.prompt) as DocNode)
						: rovoParams.prompt;

					if (rovoParams.insertPrompt === 'highlight-action') {
						// URL-param highlight actions are emitted from the Confluence view-page
						// surface, so this context is intentionally page-only.
						insertHighlightActionsEditorPlaceholder(
							prompt as DocNode,
							HIGHLIGHT_ACTION_INLINE_VIEW_PAGE_CREATION_CONTEXT_PARAMS,
						);
					} else {
						dispatch(
							sendMessage({
								prompt: encodeAdf(prompt),
								agentId: rovoParams.agentId,
								// Use the user's preference for storing messages
								// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
								// value when BE user settings are fully rolled out; BE overrides it via the capability header.
								storeUserMessages: getStoreUserMessagesPreference?.() ?? false,
								messageSources: {
									interactionSource: 'urlParam',
								},
							}),
						);
					}
				}

				// for UFO performance tracking dimensions
				addUFOCustomData({
					isEmptyConversation: !rovoParams.conversationId,
					hasInitialPrompt: !!rovoParams.prompt,
				});
			} else if (autoSelectConversation) {
				const timeoutBeforeNewChat = getTimeoutBeforeExpiration();
				// HOT-303866: a since-reverted change stored a stringified JSON object under
				// this key instead of a plain conversation ID. If we detect that corrupt object
				// shape, self-heal by overwriting the key and start a new conversation.
				const getSafeStoredConversationId = (): string => {
					const key = getChatLoadPreferenceKey(siteId);
					const raw = localStorage?.getItem(key) ?? null;
					if (!raw) {
						return EMPTY_CONVERSATION_ID;
					}
					let isCorruptObject = false;
					try {
						const parsed: unknown = JSON.parse(raw);
						isCorruptObject = typeof parsed === 'object' && parsed !== null;
					} catch {
						// Not JSON — a plain conversation ID string, use it as-is.
					}
					if (isCorruptObject) {
						localStorage?.setItem(key, EMPTY_CONVERSATION_ID);
						return EMPTY_CONVERSATION_ID;
					}
					return raw;
				};
				const targetConversationId = fg('rovo_chat_safe_conversation_id_from_ls')
					? getSafeStoredConversationId()
					: (localStorage?.getItem(getChatLoadPreferenceKey(siteId)) ?? EMPTY_CONVERSATION_ID);
				const lastMessageSentAt = getLastMessageSentAt();
				const hasTargetChanged = targetConversationId !== currentConversationId;
				const isTargetExpired =
					lastMessageSentAt && Date.now() - lastMessageSentAt >= timeoutBeforeNewChat;
				const isTargetConversationEmpty = isEmptyConversation(targetConversationId);
				const hasLocalTargetConversation = Boolean(getState().conversations[targetConversationId]);

				// start new conversation: if recent expired
				if (isTargetExpired) {
					dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
					setLastMessageSentAt(null);
					// start new conversation: if target conversation was set to new (e.g. in another tab)
				} else if (hasTargetChanged && isTargetConversationEmpty) {
					dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
					// select existing conversation: if convo exists in memory
				} else if (hasTargetChanged && hasLocalTargetConversation) {
					dispatch(setSelectedConversation({ conversationId: targetConversationId }));
					// create a new optimistic conversation: renders ChatHistory which triggers API call to fetch messages
				} else if (hasTargetChanged) {
					dispatch(createConversationOptimistically({ id: targetConversationId }));
				}

				// for UFO performance tracking dimensions
				addUFOCustomData({ isEmptyConversation: !isTargetExpired && hasTargetChanged });
			}

			// Set online status later, to ensure that pubsub listener doesn't race condition with store init state
			setChatStatus('online');

			experienceTracker.succeed({ name: ExperienceName.HYDRATE_CONVERSATION });
		},
	{
		experienceName: () => ExperienceName.HYDRATE_CONVERSATION,
	},
);

const addAgentMessage = createSafeAction(
	StoreActionLoggableName.addAgentMessage,
	({
		conversationId,
		agentNamedId,
		content,
	}: {
		agentNamedId: string;
		conversationId: string;
		content: string;
	}): ChatStoreAction =>
		({ dispatch }) => {
			dispatch(
				addAssistantMessage({
					id: generateId(),
					content,
					conversationId,
					agentNamedId,
				}),
			);
		},
);

const handleChatOpenPubsubAction = createSafeAction(
	StoreActionLoggableName.handleChatOpenPubsubAction,
	({ conversationId, agentId }: { conversationId: string; agentId?: string }): ChatStoreAction =>
		async ({ getState, dispatch }) => {
			// IF convo id is the current chat
			// refetch the chat, do not create new one
			if (conversationId === getState().currentConversationId) {
				// Keep the agent id from this `chat-new` event. When the new selected-agent
				// identity model is off, preserve the older hydration gate as the kill switch.
				const shouldStoreChatNewAgentId = !!agentId && fg('rovo_chat_agent_selection');
				if (shouldStoreChatNewAgentId) {
					const conversationAgentFromChatNew = fg('rovo_chat_agent_selection')
						? createProvisionalConversationAgent(agentId)
						: undefined;
					const conversationAgentFieldsFromChatNew = conversationAgentFromChatNew
						? { agent: conversationAgentFromChatNew }
						: {};
					dispatch(
						setConversationData({
							conversationId,
							data: {
								sourceAgentId: agentId,
								...conversationAgentFieldsFromChatNew,
							},
						}),
					);
				}
				dispatch(fetchConversation({ conversationId }));
				return;
			}

			// IF chat already exist in conversation
			// switch to that convo -> and it will trigger refetch when ChatHistory is rendered
			if (getState().conversations[conversationId]) {
				// Keep the agent id from this `chat-new` event. When the new selected-agent
				// identity model is off, preserve the older hydration gate as the kill switch.
				const shouldStoreChatNewAgentId = !!agentId && fg('rovo_chat_agent_selection');
				if (shouldStoreChatNewAgentId) {
					const conversationAgentFromChatNew = fg('rovo_chat_agent_selection')
						? createProvisionalConversationAgent(agentId)
						: undefined;
					const conversationAgentFieldsFromChatNew = conversationAgentFromChatNew
						? { agent: conversationAgentFromChatNew }
						: {};
					dispatch(
						setConversationData({
							conversationId,
							data: {
								sourceAgentId: agentId,
								...conversationAgentFieldsFromChatNew,
							},
						}),
					);
				}
				dispatch(
					setSelectedConversation({
						conversationId,
					}),
				);
				return;
			}

			// IF chat does not exist yet
			// Create conversation optimistically, which will switch to that conversation
			// By switching conversation, it will render ChatHistory
			// ChatHistory will auto fetch the chat data
			// if not found during `sendMessage`, it will auto create one and retry when encountered `404`
			// Create conversation optimistically and store sourceAgentId
			const sourceAgentIdFromChatNew =
				agentId && fg('rovo_chat_agent_selection') ? agentId : undefined;
			dispatch(
				createConversationOptimistically({
					id: conversationId,
					sourceAgentId: sourceAgentIdFromChatNew,
				}),
			);
		},
);

const createConversationOptimistically = createSafeAction(
	StoreActionLoggableName.createConversationOptimistically,
	(options: {
		name?: string;
		sourceId?: string;
		id?: string;
		timeCreated?: string;
		lastMessageTimestamp?: string;
		sourceAgentId?: string;
		status?: CHAT_STORE_STATUS;
		lastMessageType?: MessageType[];
		lastAgentMessageStatus?: LastAgentMessageStatus;
		agent?: AgentMetadata;
		agentVersion?: AgentVersion;
		rovoSpaceId?: string;
		scopes?: ConversationChannelScope[];
	}): ChatStoreAction =>
		async ({ dispatch }, { setMessages }) => {
			const conversationId = options?.id ?? EMPTY_CONVERSATION_ID;
			const time = new Date().toISOString();
			// Seed `conversation.agent` from the known optimistic conversation identity.
			const agentForOptimisticConversation = resolveConversationSeedAgent({
				backendAgent: options?.agent,
				agentId: options?.sourceAgentId,
				legacyAgent: options?.agent,
			});
			// Under the selected-agent gate the header derives the displayed agent
			// solely from `conversation.agent`. When this optimistic creation re-uses an
			// existing conversation slot (e.g. EMPTY_CONVERSATION_ID) the data is
			// shallow-merged, so omitting the `agent` key would leave a previously-seeded
			// specialist agent in place. Selecting "Chat with Rovo" (no agent id) must
			// therefore explicitly clear `conversation.agent` back to the default Rovo
			// agent rather than silently preserving the stale one.
			const optimisticConversationAgentData = agentForOptimisticConversation
				? { agent: agentForOptimisticConversation }
				: fg('rovo_chat_agent_selection')
					? { agent: undefined } // explicit clear so "Chat with Rovo" resets a stale specialist
					: {};

			dispatch(
				initializeConversationData({
					conversationId,
					data: {
						id: conversationId,
						lastMessageTimestamp: options.lastMessageTimestamp ?? time,
						name: options.name ?? generateConversationName(),
						sourceAgentId: options.sourceAgentId,
						sourceId: options.sourceId ?? undefined,
						status: options?.status ?? CHAT_STORE_STATUS.idle,
						timeCreated: options?.timeCreated ?? time,
						traces: [],
						messages: [],
						lastMessageType: options?.lastMessageType ?? [],
						lastAgentMessageStatus: options?.lastAgentMessageStatus,
						// Explicitly set rovoSpaceId (including undefined) so that re-initialising
						// EMPTY_CONVERSATION_ID without a space clears any stale rovoSpaceId left
						// from a previous space-scoped conversation.
						rovoSpaceId: options?.rovoSpaceId,
						...optimisticConversationAgentData,
						...(options?.agentVersion ? { agentVersion: options.agentVersion } : {}),
						...(options?.scopes ? { scopes: options.scopes } : {}),
					},
				}),
			);

			setMessages(conversationId, []);

			dispatch(
				setSelectedConversation({
					conversationId,
				}),
			);
		},
);

const createSeededConversation = createSafeAction(
	StoreActionLoggableName.createSeededConversation,
	(options: {
		name?: string;
		dialogues: Dialogues[];
		sourceId?: string;
		id?: string;
		followUpSource: Omit<FollowUpSource, 'none'>;
		agentId?: string;
		agentVersion?: AgentVersion;
		tags?: string[];
		skipNextFetch?: boolean;
		// When the seeded conversation is scoped to a Rovo Space (e.g. created from
		// the SpaceLandingPage chat input or a space starter), set this so the
		// conversation appears under the space in the side nav and is excluded from
		// the regular conversation list immediately on optimistic creation.
		rovoSpaceId?: string;
		mode?: {
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			fastModeEnabled?: boolean;
			taskModeEnabled?: boolean;
			webSearchEnabled?: boolean;
			useCurrentPageContext?: boolean;
			appFilters?: RovoApp[];
		};
	}): ChatStoreAction<Promise<string | undefined>> =>
		async (
			{ dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				setMessages,
				getChatInitiator,
				onNewConversationStartingWithSendMessage,
			},
		) => {
			experienceTracker.start({
				name: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
				id: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
			});

			const createdConversation = await assistanceService.createConversationChannel(options);
			const humanMessage = createdConversation.messages?.find((message) => isHumanMessage(message));
			const assistantMessage = createdConversation.messages?.find((message) =>
				isAssistantMessage(message),
			);
			const humanMessageId = humanMessage ? humanMessage.id : '';
			const assistantMessageId = assistantMessage ? assistantMessage.id : '';
			const conversationTags = createdConversation.tags ?? options.tags;

			// The header reads the displayed agent only from `conversation.agent.agent_id`,
			// so the chat-new/seeded flow must seed it from the known agent id (preferring a
			// backend-returned agent). Without this, a seeded conversation opened with a
			// specific agent shows default Rovo.
			const seededConversationAgent = resolveConversationSeedAgent({
				backendAgent: createdConversation.agent,
				agentId: options.agentId,
				legacyAgent: createdConversation.agent,
			});
			const seededConversationAgentFields = seededConversationAgent
				? { agent: seededConversationAgent }
				: {};

			dispatch(
				initializeConversationData({
					conversationId: createdConversation.id,
					data: {
						id: createdConversation.id,
						name: createdConversation.name,
						timeCreated: createdConversation.time_created,
						status: CHAT_STORE_STATUS.idle,
						traces: [],
						sourceId: options.sourceId ?? undefined,
						sourceAgentId: options.agentId,
						agentVersion: options.agentVersion,
						messages: [],
						...seededConversationAgentFields,
						...(conversationTags !== undefined ? { tags: conversationTags } : {}),
						...(options.skipNextFetch ? { skipNextFetch: true } : {}),
						// Scope the seeded conversation to a Rovo Space when applicable so the
						// nav can immediately render it under the correct space and exclude it
						// from the regular conversation list (see ChatListMenuItems filter).
						...(options.rovoSpaceId && fg('rovo_chat_spaces_fe')
							? { rovoSpaceId: options.rovoSpaceId }
							: {}),
					},
				}),
			);

			const chatInitiator = getChatInitiator();

			// Build synthetic ChatMessage[] from the seeded dialogues and pass directly to
			// processConversationMessages, which handles all boundary content extraction
			// (lumina, chart, expand, sculptor) consistently with fetchConversation —
			// without an extra getChat network call.
			const syntheticMessages: ChatMessage[] = options.dialogues.flatMap((dialogue) => {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'sent',
					actionSubject: 'message',
					attributes: {
						messageId: humanMessageId,
						interactionSource: 'movedOver' as InteractionSource,
						followUpSource: options.followUpSource,
						timestamp: new Date().toISOString(),
						initiator: chatInitiator,
					},
				});
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'received',
					actionSubject: 'agentMessage',
					attributes: {
						messageId: assistantMessageId,
						humanMessageId,
						pluginsInvoked: [],
						hasSources: false,
						timestamp: new Date().toISOString(),
						initiator: chatInitiator,
					},
				});

				// When mimeType is 'text/adf', parse the ADF JSON string into a DocNode
				// so encodeAdf's isAdfContent check can pass it through natively.
				let agentContent: string | DocNode = dialogue.agent_message.content;
				if (dialogue.agent_message.mimeType === 'text/adf') {
					try {
						agentContent = JSON.parse(dialogue.agent_message.content);
					} catch {
						// If parsing fails, fall back to raw string content
					}
				}

				// Use raw ChatMessage types (not createStoreHuman/createStoreAssistant) so that
				// processConversationMessages can apply all boundary content extraction
				// (lumina, chart, expand, sculptor) before producing the final StoreChatItem[].
				const humanMessage: ChatMessage = {
					role: 'HUMAN',
					id: generateId(),
					content: dialogue.human_message.content,
					time_created: new Date().toISOString(),
					user_ari: 'unknown',
					plugin_invocations: [],
					isFromSeededConversation: true,
				};
				const assistantStreamResponse: StreamResponse = {
					type: 'FINAL_RESPONSE',
					message: {
						message: {
							role: 'ASSISTANT',
							id: generateId(),
							conversation_channel_id: createdConversation.id,
							content: agentContent,
							time_created: new Date().toISOString(),
							user_ari: 'unknown',
							plugin_invocations: [],
							author: {
								named_id: 'ai_mate_agent',
								name: 'unknown',
								actor_type: 'AGENT',
								ari: null,
								id: 'unknown',
							},
						},
					},
				};

				return [humanMessage, assistantStreamResponse];
			});

			const processedMessages = processConversationMessages({
				latestMessages: syntheticMessages,
				conversationId: createdConversation.id,
				sendAnalyticsEvent,
			});

			setMessages(createdConversation.id, processedMessages);

			dispatch(
				setSelectedConversation({
					conversationId: createdConversation.id,
				}),
			);

			if (options.mode && onNewConversationStartingWithSendMessage) {
				onNewConversationStartingWithSendMessage(createdConversation.id, options.mode);
			}

			experienceTracker.succeed({
				name: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
			});

			return createdConversation.id;
		},
	{
		experienceName: () => ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
	},
);

/** Side-nav list items may attach UI-only fields; the chat store `Conversation` type must not persist them. */
const conversationMetadataForStore = (
	conversation: Conversation & { isEmpty?: unknown; currentTask?: unknown },
): Conversation => {
	const { isEmpty: _isEmpty, currentTask: _currentTask, ...storeConversation } = conversation;
	return storeConversation;
};

const setSelectedConversation = createSafeAction(
	StoreActionLoggableName.setSelectedConversation,
	({
		conversationId,
		sourceAgentId,
		conversationMetadata,
		targetMessageId,
	}: {
		conversationId: string;
		sourceAgentId?: string;
		/**
		 * When the id is not yet in `conversations` (e.g. opened from space-scoped GET list),
		 * seed the store from this payload so selection is not downgraded to empty chat.
		 */
		conversationMetadata?: Conversation & { isEmpty?: unknown; currentTask?: unknown };
		/**
		 * When set, ChatHistory will scroll to this specific message after the conversation loads.
		 * Used by Go to Chat from the Tasks List to route to the task's completion/current message.
		 */
		targetMessageId?: string | null;
	}): ChatStoreAction =>
		({ setState, getState, dispatch }, { siteId }) => {
			let resolvedConversationId = conversationId;

			if (!getState().conversations[conversationId]) {
				if (conversationMetadata?.id === conversationId) {
					dispatch(
						initializeConversationData({
							conversationId,
							data: {
								...conversationMetadataForStore(conversationMetadata),
								...(targetMessageId ? { targetTaskMessageId: targetMessageId } : {}),
							},
						}),
					);
				} else {
					resolvedConversationId = EMPTY_CONVERSATION_ID;
				}
			} else if (targetMessageId) {
				dispatch(
					setConversationData({
						conversationId,
						data: { targetTaskMessageId: targetMessageId },
					}),
				);
			}

			setState({
				currentConversationId: resolvedConversationId,
			});

			// Keep the agent id provided when selecting this conversation; gate-on also seeds `conversation.agent` for the header.
			if (sourceAgentId) {
				const conversationAgentFromSource = fg('rovo_chat_agent_selection')
					? createProvisionalConversationAgent(sourceAgentId)
					: undefined;
				const conversationAgentSourceData = conversationAgentFromSource
					? { agent: conversationAgentFromSource }
					: {};
				dispatch(
					setConversationData({
						conversationId: resolvedConversationId,
						data: {
							sourceAgentId,
							...conversationAgentSourceData,
						},
					}),
				);
			}

			// Store preference.
			// For space-scoped conversations, store EMPTY_CONVERSATION_ID instead of the
			// client-generated UUID. The client UUID is never returned by the backend /conversations
			// API, so restoring it on refresh creates an orphaned optimistic entry that has no
			// rovoSpaceId and cannot be matched to any API channel — causing a ghost item to
			// appear in the regular conversation list. Starting fresh (empty chat) on refresh
			// is the correct behaviour for space conversations.
			const conversationForStorage = getState().conversations[resolvedConversationId];
			const localStorageConversationId =
				conversationForStorage?.rovoSpaceId && fg('rovo_chat_spaces_fe')
					? EMPTY_CONVERSATION_ID
					: resolvedConversationId;
			localStorage?.setItem(getChatLoadPreferenceKey(siteId), localStorageConversationId);
		},
);

const updateActionInvocationStatus = createSafeAction(
	StoreActionLoggableName.updateActionInvocationStatus,
	({
		conversationId,
		messageId,
		invocationId,
		status,
	}: {
		conversationId: string;
		messageId: string;
		invocationId: string;
		status: MessageActionInvocationStatus;
	}): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const agentMessage = getAgentMessageById(messages, messageId);

			if (!agentMessage.result) {
				return;
			}

			const updatedMessages = [...messages];

			updatedMessages[agentMessage.index] = {
				...agentMessage.result,
				invokedActions: {
					...agentMessage.result.invokedActions,
					[invocationId]: status,
				},
			};

			setMessages(conversationId, updatedMessages);
		},
);

const updateConfirmedToolsActionStatuses = createSafeAction(
	StoreActionLoggableName.updateActionInvocationStatus,
	({
		conversationId,
		messageId,
		tools,
		status,
	}: {
		conversationId: string;
		messageId: string;
		tools: RovoLabsConfirmedTools['tools'];
		status: MessageActionInvocationStatus | 'fromResolution';
	}): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const agentMessage = getAgentMessageById(messages, messageId);

			if (!agentMessage.result) {
				return;
			}

			const updatedInvokedActions = { ...agentMessage.result.invokedActions };
			for (const tool of tools) {
				const resolvedStatus: MessageActionInvocationStatus =
					status === 'fromResolution'
						? tool.resolution?.type === 'cancelled'
							? 'cancelled'
							: 'finished'
						: status;
				updatedInvokedActions[tool.invocation_id] = resolvedStatus;
			}

			const updatedMessages = [...messages];
			updatedMessages[agentMessage.index] = {
				...agentMessage.result,
				invokedActions: updatedInvokedActions,
			};

			setMessages(conversationId, updatedMessages);
		},
);

const updateActionData = createSafeAction(
	StoreActionLoggableName.updateActionData,
	({
		conversationId,
		messageId,
		invocationId,
		data,
	}: {
		conversationId: string;
		messageId: string;
		invocationId: string;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		data: any;
	}): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const agentMessage = getAgentMessageById(messages, messageId);

			if (!agentMessage.result) {
				return;
			}

			const updatedMessages = [...messages];

			agentMessage.result.content.message.message.actions?.forEach((action) => {
				if (action.invocationId === invocationId) {
					action.data = {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						...(action.data as any),
						...data,
					};
				}
			});

			updatedMessages[agentMessage.index] = agentMessage.result;

			setMessages(conversationId, updatedMessages);
		},
);

const updateConversation =
	({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Partial<Pick<Conversation, 'name' | 'readStatus' | 'isPinned' | 'pinnedAt'>>;
	}): ChatStoreAction =>
	async ({ dispatch }, { assistanceService, experienceTracker }) => {
		experienceTracker.start({
			name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
			id: conversationId,
			attributes: {
				conversationId,
			},
		});

		let updatedConversation;
		try {
			updatedConversation = await assistanceService?.updateConversationChannel(
				conversationId,
				data,
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			experienceTracker.fail({
				name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
				error,
			});

			throw error;
		}

		const nextData = { ...data };
		if (updatedConversation) {
			if (updatedConversation.name !== undefined) {
				nextData.name = updatedConversation.name;
			}
			if (updatedConversation.readStatus !== undefined) {
				nextData.readStatus = updatedConversation.readStatus;
			}
			if (typeof updatedConversation.isPinned === 'boolean') {
				nextData.isPinned = updatedConversation.isPinned;
			}
			if (updatedConversation.pinnedAt !== undefined) {
				nextData.pinnedAt = updatedConversation.pinnedAt;
			}
		}

		dispatch(
			setConversationData({
				conversationId,
				data: nextData,
			}),
		);

		experienceTracker.succeed({
			name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
		});
	};

function getScheduleRovoChatRuleIdUuidFromExecutionMessage(message: Message): string | undefined {
	const raw = message.message_metadata?.ruleIdUuid;
	if (raw === undefined || raw === null) {
		return undefined;
	}
	const s = typeof raw === 'string' ? raw.trim() : String(raw);
	return s.length > 0 ? s : undefined;
}

function getScheduleRovoChatRRuleFromExecutionMessage(message: Message): string | undefined {
	const raw = message.message_metadata?.rRule;
	if (raw === undefined || raw === null) {
		return undefined;
	}
	const s = typeof raw === 'string' ? raw.trim() : String(raw);
	return s.length > 0 ? s : undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResolveConversationActionArgs<TActionResult = any> = {
	conversationId: string;
	messageId: string;
	payload: MessageActionPayload;
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
	/** Skip tracking experience when called from bulk action (to avoid noise from individual failures) */
	skipExperienceTracking?: boolean;
	onError?: OnErrorCallback<ResolveConversationActionArgs<TActionResult>>;
};

const resolveConversationAction = createSafeAction<
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(args: ResolveConversationActionArgs) => any,
	undefined
>(
	StoreActionLoggableName.resolveConversationAction,
	({
		conversationId,
		messageId,
		payload,
		actionContentProcessor,
		skipSuccessOptimisticAssistantResponse = false,
		skipExperienceTracking = false,
	}) =>
		async <TActionResult>(
			{ dispatch, getState }: StoreActionApi<ChatStorePrivateState>,
			{
				assistanceService,
				experienceTracker,
				trackAIMAUEvent,
				sendAnalyticsEvent,
				getMessages,
				broadcastConfirmedActions,
				chatContextStoreActions,
			}: ChatStoreContainerProps,
		): Promise<TActionResult | undefined> => {
			let actionCallbackData: TActionResult | undefined;
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const humanMessageId = getAssociatedHumanMessageId(getMessages(conversationId), messageId);
			const featureArea = chatContextStoreActions.getIsStagingArea()
				? 'aifc-staging-area'
				: 'unknown';

			const attributes = {
				conversationId,
				resolution: payload.context.resolution,
				actionKey: payload.context.key,
			};

			if (!skipExperienceTracking) {
				experienceTracker.start({
					name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
					id: conversationId,
					attributes,
				});
			}

			try {
				const { invocationId } = payload.context;

				// Set actions loading state
				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId,
						invocationId,
						status: 'started',
					}),
				);

				const response = await assistanceService.resolveConversationAction(conversationId, payload);
				const { message } = response;

				dispatch(
					updateActionData({
						conversationId,
						messageId,
						invocationId,
						data: payload.context.data,
					}),
				);

				const ruleIdUuidFromExecution =
					payload.context.key === SCHEDULED_CHAT_KEY && isAssistantMessage(message)
						? getScheduleRovoChatRuleIdUuidFromExecutionMessage(message)
						: undefined;
				const rRuleFromExecution =
					payload.context.key === SCHEDULED_CHAT_KEY && isAssistantMessage(message)
						? getScheduleRovoChatRRuleFromExecutionMessage(message)
						: undefined;
				if (ruleIdUuidFromExecution || rRuleFromExecution) {
					const previousData = payload.context.data;
					const harvested = {
						...(ruleIdUuidFromExecution ? { ruleIdUuid: ruleIdUuidFromExecution } : {}),
						...(rRuleFromExecution ? { rRule: rRuleFromExecution } : {}),
					};
					dispatch(
						updateActionData({
							conversationId,
							messageId,
							invocationId,
							data:
								typeof previousData === 'object' && previousData !== null
									? { ...previousData, ...harvested }
									: harvested,
						}),
					);
				}

				const isMessageWithContent = !!message.content;

				// Display a new agent message from the response to reflect the change in chat history
				if (
					isAssistantMessage(message) &&
					(isMessageWithContent || isActionExecutedSuccessfully(message))
				) {
					if (isMessageWithContent && !skipSuccessOptimisticAssistantResponse) {
						dispatch(
							addAssistantMessage({
								agentNamedId: message.author?.named_id,
								content: message.content,
								conversationId,
								id: message.id,
							}),
						);
					}

					actionCallbackData = actionContentProcessor?.(message) ?? undefined;

					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId,
							status: deriveInvocationStatus(message, payload.context),
						}),
					);

					if (isActionExecutedSuccessfully(message)) {
						broadcastConfirmedActions?.(conversationId, [payload.context]);
					}

					sendAnalyticsEvent({
						eventType: 'ui',
						action: 'success',
						actionSubject: 'rovoChatMessageAction',
						attributes: {
							recipientAgentNamedId: payload.context.recipient_agent_named_id,
							actionKey: payload.context.key,
							resolutionType: payload.context.resolution.type,
							featureArea,
							humanMessageId,
							messageId,
						},
					});
				} else {
					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId: payload.context.invocationId,
							status: 'errored',
						}),
					);

					sendAnalyticsEvent({
						eventType: 'track',
						action: 'error',
						actionSubject: 'rovoChatMessageAction',
						attributes: {
							recipientAgentNamedId: payload.context.recipient_agent_named_id,
							actionKey: payload.context.key,
							resolutionType: payload.context.resolution.type,
							featureArea,
							humanMessageId,
							messageId,
						},
					});
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (!skipExperienceTracking) {
					experienceTracker.fail({
						name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
						error,
						attributes,
					});
				}

				trackAIMAUEvent({
					type: 'error',
					userGenerated: true,
					source: 'messageActionsResolver',
					subjectId: payload.context.resolution.type,
					singleInstrumentationID: messageId,
				});

				sendAnalyticsEvent({
					eventType: 'track',
					action: 'error',
					actionSubject: 'rovoChatMessageAction',
					attributes: {
						recipientAgentNamedId: payload.context.recipient_agent_named_id,
						actionKey: payload.context.key,
						resolutionType: payload.context.resolution.type,
						featureArea,
						humanMessageId,
						messageId,
					},
				});

				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId,
						invocationId: payload.context.invocationId,
						status: 'errored',
					}),
				);

				throw new MessageActionError({
					message: `Message action error: ${error.message} for actionKey: ${payload.context.key}`,
					conversationId,
					messageId: messageId,
					invocationId: payload.context.invocationId,
					actionKey: payload.context.key,
					cause: error,
				});
			}

			if (!skipExperienceTracking) {
				experienceTracker.succeed({
					name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
					attributes,
				});
			}
			return actionCallbackData;
		},
	{
		actionErrorHandler: (context) => {
			const { onError, ...args } = context.args?.[0] ?? {};
			return onError ? onError({ ...context, args }) : { proceedToDefaultHandling: true };
		},
	},
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BulkResolveConversationActionsArgs<TActionResult = any> = {
	conversationId: string;
	messageId: string;
	payloads: MessageActionPayload[];
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
};

const bulkResolveConversationActions = createSafeAction<
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(args: BulkResolveConversationActionsArgs) => any,
	undefined
>(
	StoreActionLoggableName.bulkResolveConversationActions,
	({
		conversationId,
		messageId,
		payloads,
		actionContentProcessor,
		skipSuccessOptimisticAssistantResponse = false,
	}) =>
		async <TActionResult>(
			{ dispatch, getState }: StoreActionApi<ChatStorePrivateState>,
			{ experienceTracker }: ChatStoreContainerProps,
		): Promise<(TActionResult | undefined)[]> => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return [];
			}

			experienceTracker.start({
				name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
				id: conversationId,
				attributes: {
					conversationId,
					actionCount: payloads.length,
				},
			});

			// Execute all actions sequentially
			const actionCallbackResults: (TActionResult | undefined)[] = [];
			const successfulActions: MessageActionPayload['context'][] = [];
			let hasErrors = false;
			let successCount = 0;

			for (const payload of payloads) {
				const result = await dispatch(
					resolveConversationAction({
						conversationId,
						messageId,
						payload,
						actionContentProcessor,
						skipSuccessOptimisticAssistantResponse,
						skipExperienceTracking: true,
					}),
				);

				actionCallbackResults.push(result);

				// Track success: result is defined means action succeeded
				if (result !== undefined) {
					successCount++;
					successfulActions.push(payload.context);
				} else if (!actionContentProcessor) {
					// Action with no processor returns undefined on success
					successCount++;
				} else {
					// Action with processor returned undefined = failure
					hasErrors = true;
				}
			}

			if (hasErrors) {
				experienceTracker.fail({
					name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
					error: new Error('Some bulk actions failed'),
					attributes: {
						conversationId,
						actionCount: payloads.length,
						successCount,
						failureCount: payloads.length - successCount,
					},
				});
			} else {
				experienceTracker.succeed({
					name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
					attributes: {
						conversationId,
						actionCount: payloads.length,
					},
				});
			}

			return actionCallbackResults;
		},
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BulkResolveConversationUIActionsArgs<TActionResult = any> = {
	conversationId: string;
	messageId: string;
	payloads: MessageActionPayload[];
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
};

const bulkResolveConversationUIActions = createSafeAction<
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(args: BulkResolveConversationUIActionsArgs) => any,
	undefined
>(
	StoreActionLoggableName.bulkResolveConversationUIActions,
	({
		conversationId,
		messageId,
		payloads,
		actionContentProcessor,
		skipSuccessOptimisticAssistantResponse = false,
	}) =>
		async <TActionResult>(
			{ dispatch, getState }: StoreActionApi<ChatStorePrivateState>,
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				getMessages,
				broadcastConfirmedActions,
				chatContextStoreActions,
			}: ChatStoreContainerProps,
		): Promise<(TActionResult | undefined)[]> => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return [];
			}

			const humanMessageId = getAssociatedHumanMessageId(getMessages(conversationId), messageId);
			const featureArea = chatContextStoreActions.getIsStagingArea()
				? 'aifc-staging-area'
				: 'unknown';

			experienceTracker.start({
				name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
				id: conversationId,
				attributes: {
					conversationId,
					actionCount: payloads.length,
				},
			});

			// Set all invocation statuses to 'started'
			for (const payload of payloads) {
				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId,
						invocationId: payload.context.invocationId,
						status: 'started',
					}),
				);
			}

			try {
				const responses = await assistanceService.bulkResolveConversationUIActions(
					conversationId,
					messageId,
					{ items: payloads },
				);

				const actionCallbackResults: (TActionResult | undefined)[] = [];
				const successfulActions: MessageActionPayload['context'][] = [];
				let hasErrors = false;
				let successCount = 0;

				// Process each response paired with its corresponding payload
				for (let i = 0; i < payloads.length; i++) {
					const payload = payloads[i];
					const response = responses[i];
					const { invocationId } = payload.context;

					if (!response) {
						// No response for this item - mark as errored
						dispatch(
							updateActionInvocationStatus({
								conversationId,
								messageId,
								invocationId,
								status: 'errored',
							}),
						);
						hasErrors = true;
						actionCallbackResults.push(undefined);

						sendAnalyticsEvent({
							eventType: 'track',
							action: 'error',
							actionSubject: 'rovoChatMessageAction',
							attributes: {
								recipientAgentNamedId: payload.context.recipient_agent_named_id,
								actionKey: payload.context.key,
								resolutionType: payload.context.resolution.type,
								featureArea,
								humanMessageId,
								messageId,
							},
						});
						continue;
					}

					const { message } = response;

					dispatch(
						updateActionData({
							conversationId,
							messageId,
							invocationId,
							data: payload.context.data,
						}),
					);

					const isMessageWithContent = !!message.content;

					if (
						isAssistantMessage(message) &&
						(isMessageWithContent || isActionExecutedSuccessfully(message))
					) {
						if (isMessageWithContent && !skipSuccessOptimisticAssistantResponse) {
							dispatch(
								addAssistantMessage({
									agentNamedId: message.author?.named_id,
									content: message.content,
									conversationId,
									id: message.id,
								}),
							);
						}

						const callbackData = actionContentProcessor?.(message) ?? undefined;
						actionCallbackResults.push(callbackData);

						dispatch(
							updateActionInvocationStatus({
								conversationId,
								messageId,
								invocationId,
								status: deriveInvocationStatus(message, payload.context),
							}),
						);

						if (isActionExecutedSuccessfully(message)) {
							successfulActions.push(payload.context);
						}

						successCount++;

						sendAnalyticsEvent({
							eventType: 'ui',
							action: 'success',
							actionSubject: 'rovoChatMessageAction',
							attributes: {
								recipientAgentNamedId: payload.context.recipient_agent_named_id,
								actionKey: payload.context.key,
								resolutionType: payload.context.resolution.type,
								featureArea,
								humanMessageId,
								messageId,
							},
						});
					} else {
						dispatch(
							updateActionInvocationStatus({
								conversationId,
								messageId,
								invocationId,
								status: 'errored',
							}),
						);
						hasErrors = true;
						actionCallbackResults.push(undefined);

						sendAnalyticsEvent({
							eventType: 'track',
							action: 'error',
							actionSubject: 'rovoChatMessageAction',
							attributes: {
								recipientAgentNamedId: payload.context.recipient_agent_named_id,
								actionKey: payload.context.key,
								resolutionType: payload.context.resolution.type,
								featureArea,
								humanMessageId,
								messageId,
							},
						});
					}
				}

				// Broadcast all successful confirmed actions at once
				if (successfulActions.length > 0) {
					broadcastConfirmedActions?.(conversationId, successfulActions);
				}

				if (hasErrors) {
					experienceTracker.fail({
						name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
						error: new Error('Some bulk actions failed'),
						attributes: {
							conversationId,
							actionCount: payloads.length,
							successCount,
							failureCount: payloads.length - successCount,
						},
					});
				} else {
					experienceTracker.succeed({
						name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
						attributes: {
							conversationId,
							actionCount: payloads.length,
						},
					});
				}

				return actionCallbackResults;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				// If the entire bulk request fails, mark all invocations as errored
				for (const payload of payloads) {
					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId: payload.context.invocationId,
							status: 'errored',
						}),
					);
				}

				experienceTracker.fail({
					name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
					error: error instanceof Error ? error : new Error('Bulk resolve failed'),
					attributes: {
						conversationId,
						actionCount: payloads.length,
						successCount: 0,
						failureCount: payloads.length,
					},
				});

				throw error;
			}
		},
);

const deleteConversation =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	async ({ dispatch, getState }, { assistanceService, experienceTracker }) => {
		experienceTracker.start({
			name: ExperienceName.CLEAR_CHAT,
			id: conversationId,
			attributes: {
				conversationId,
			},
		});

		await getState().conversations[conversationId]?.storeAbortController?.abort();

		try {
			await assistanceService.deleteChat(conversationId);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			experienceTracker.fail({
				name: ExperienceName.CLEAR_CHAT,
				error,
			});

			throw error;
		}

		// Remove the deleted conversation from the local store prior to refetching channels.
		// fetchConversationChannelsData preserves any local conversation whose ID is missing
		// from the API response (to keep optimistic / in-flight conversations alive). Without
		// this explicit removal, the just-deleted conversation would be treated as
		// "optimistic" and re-added to the store, causing it to reappear in the UI.
		dispatch(removeConversationFromStore({ conversationId }));

		await dispatch(fetchConversationChannelsData({ reconcileFirstPage: true }));

		experienceTracker.succeed({
			name: ExperienceName.CLEAR_CHAT,
		});
	};

/**
 * ************************************************************
 * Store
 * ************************************************************
 */

type WarmConversationCacheArgs = {
	conversation: {
		id: string;
		name?: string;
		timeCreated?: string;
		agentId?: string;
	};
	forceRefresh?: boolean;
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

const warmConversationCache = createSafeAction(
	StoreActionLoggableName.warmConversationCache,
	({
		conversation: conversationParams,
		forceRefresh,
		onSuccess,
		onError,
	}: WarmConversationCacheArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{ assistanceService, experienceTracker, sendAnalyticsEvent, setMessages, capabilities },
		) => {
			const conversationId = conversationParams.id;

			const existingConversation = getState().conversations[conversationId];

			// Skip if conversation is actively in-flight
			if (
				existingConversation?.status === CHAT_STORE_STATUS.loadingChat ||
				existingConversation?.status === CHAT_STORE_STATUS.awaitingResponse ||
				existingConversation?.status === CHAT_STORE_STATUS.typingResponse
			) {
				return;
			}

			// Skip if conversation is already loaded (idle with messages) unless forceRefresh
			if (existingConversation?.status === CHAT_STORE_STATUS.idle && !forceRefresh) {
				return;
			}

			try {
				experienceTracker.start({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
					id: conversationId,
					attributes: {
						conversationId,
						forceRefresh: Boolean(forceRefresh),
					},
				});

				// Bootstrap conversation in store with provided metadata or client defaults.
				// rovo_chat_agent_selection cleanup: remove the gate when selected-agent identity
				// is fully launched. Preserve existing metadata during this loading reset,
				// or provisionally seed the agent id provided by the URL/pubsub flow so the header can hydrate it.
				const conversationAgentForWarmLoadingReset = fg('rovo_chat_agent_selection')
					? (existingConversation?.agent ??
						createProvisionalConversationAgent(
							conversationParams.agentId ?? existingConversation?.sourceAgentId,
						))
					: undefined;
				const conversationAgentFieldsForWarmLoadingReset = conversationAgentForWarmLoadingReset
					? { agent: conversationAgentForWarmLoadingReset }
					: {};

				dispatch(
					initializeConversationData({
						conversationId,
						data: {
							id: conversationId,
							name: conversationParams.name ?? existingConversation?.name ?? '',
							timeCreated:
								conversationParams.timeCreated ??
								existingConversation?.timeCreated ??
								new Date().toISOString(),
							status: CHAT_STORE_STATUS.loadingChat,
							traces: [],
							sourceAgentId:
								conversationParams.agentId ?? existingConversation?.sourceAgentId ?? '',
							...conversationAgentFieldsForWarmLoadingReset,
							messages: [],
						},
					}),
				);

				// If metadata params not fully provided, fetch from API in parallel (silent failure)
				const hasFullMetadata =
					conversationParams.name != null && conversationParams.timeCreated != null;

				const metadataPromise: Promise<void> = hasFullMetadata
					? Promise.resolve()
					: assistanceService
							.fetchConversationMetadata(conversationId)
							.then((metadata) => {
								// rovo_chat_agent_selection cleanup: remove the gate and always keep
								// backend conversation-agent metadata from fetched channels.
								const fetchedConversationAgentFields =
									metadata.agent && fg('rovo_chat_agent_selection')
										? { agent: metadata.agent }
										: {};

								dispatch(
									setConversationData({
										conversationId,
										data: {
											name: metadata.name ?? conversationParams.name ?? '',
											timeCreated:
												metadata.time_created ??
												conversationParams.timeCreated ??
												existingConversation?.timeCreated ??
												new Date().toISOString(),
											sourceAgentId:
												metadata.agent?.agent_named_id ??
												conversationParams.agentId ??
												existingConversation?.sourceAgentId ??
												'',
											...fetchedConversationAgentFields,
											...(metadata.tags !== undefined ? { tags: metadata.tags } : {}),
										},
									}),
								);
							})
							.catch(() => {
								// Silent failure — keep client defaults
							});

				// Run messages fetch and metadata fetch in parallel; wait for both
				const chatPromise = assistanceService.getChat(conversationId, capabilities);

				const [chatResult] = await Promise.allSettled([chatPromise, metadataPromise]);

				if (chatResult.status === 'rejected') {
					const normalizedError =
						chatResult.reason instanceof Error
							? chatResult.reason
							: new Error(String(chatResult.reason));
					experienceTracker.fail({
						name: ExperienceName.WARM_CONVERSATION_CACHE,
						error: normalizedError,
					});
					dispatch(
						setConversationData({
							conversationId,
							data: clearStatus({}),
						}),
					);
					onError?.(normalizedError);
					return;
				}

				const serviceChat = chatResult.value;

				const status = getState().conversations[conversationId]?.status;

				if (status !== CHAT_STORE_STATUS.loadingChat) {
					return;
				}

				const messages = processConversationMessages({
					latestMessages: serviceChat,
					conversationId,
					sendAnalyticsEvent,
					shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
						getState(),
						conversationId,
					),
				});

				const fetchedAgentVersion = extractAgentVersionFromMessages(serviceChat);

				dispatch(
					setConversationData({
						conversationId,
						data: {
							...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
							...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
						},
					}),
				);

				setMessages(conversationId, messages);

				experienceTracker.succeed({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
				});

				onSuccess?.();
			} catch (e) {
				const normalizedError = e instanceof Error ? e : new Error(String(e));
				experienceTracker.fail({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
					error: normalizedError,
				});
				onError?.(normalizedError);
			}
		},
	{
		experienceName: () => ExperienceName.WARM_CONVERSATION_CACHE,
		actionErrorHandler: () => {
			// Suppress all default error handling for cache warming
			return;
		},
	},
);

type PrefetchConversationMessagesArgs = {
	conversationId: string;
	/**
	 * When `true`, sets `skipNextFetch: true` on the conversation after a
	 * successful prefetch, suppressing the cold-path `fetchConversation` that
	 * `<ChatHistory>` dispatches on its next mount. Callers must opt-in: the
	 * default (`false`) leaves the cold-path retry/fetch path untouched, so
	 * future callers that want a pure cache-warm without poisoning the next
	 * fetch don't get surprised.
	 */
	skipNextFetch?: boolean;
	/**
	 * Fired synchronously before the wire request kicks off, but only when this
	 * call actually issues a fetch (not when an existing in-flight or already-
	 * loaded conversation short-circuits it)
	 */
	onStart?: () => void;
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

/**
 * Imperative warm-path messages prefetch
 */
const prefetchConversationMessages = createSafeAction(
	StoreActionLoggableName.prefetchConversationMessages,
	({
		conversationId,
		skipNextFetch = false,
		onStart,
		onSuccess,
		onError,
	}: PrefetchConversationMessagesArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				setMessages,
				showFlag,
				capabilities,
			},
		) => {
			const conversation = getState().conversations[conversationId];

			// Bail without emitting if the conversation isn't selectable, is
			// in flight, or is already loaded
			if (
				isEmptyConversation(conversationId) ||
				!conversation ||
				conversation.status !== CHAT_STORE_STATUS.idle ||
				conversation.messages.length > 0
			) {
				return;
			}

			onStart?.();
			experienceTracker.start({
				name: ExperienceName.PREFETCH_CHAT,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			dispatch(
				resetConversationForLoading({
					conversationId,
					traces: [],
					sourceAgentId: conversation.sourceAgentId ?? '',
				}),
			);

			try {
				const serviceChat = await assistanceService.getChat(conversationId, capabilities);

				const status = getState().conversations[conversationId]?.status;
				if (status !== CHAT_STORE_STATUS.loadingChat) {
					// Something else (e.g. a user-initiated send or an explicit
					// `fetchConversation`) raced ahead; surrender the response.
					return;
				}

				const messages = processConversationMessages({
					latestMessages: serviceChat,
					conversationId,
					sendAnalyticsEvent,
					shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
						getState(),
						conversationId,
					),
				});

				const fetchedAgentVersion = extractAgentVersionFromMessages(serviceChat);

				// Mirror fetchConversation's reconnect check.
				const lastMessage = serviceChat.at(-1);
				const isDeepResearch = (lastMessage as StreamResponse)?.message?.message?.message_metadata
					?.deep_research;
				const shouldReconnect = isInProgressStreamResponse(lastMessage);

				if (shouldReconnect) {
					const messageId = (lastMessage as StreamResponse).message.message.id;
					const updatedMessages = clearStoreAssistantMessageContent(messages, messageId);

					dispatch(
						setConversationData({
							conversationId,
							data: {
								...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
								sourceAgentId: getSourceAgentId(
									messages,
									getConversation(getState(), conversationId),
								),
								...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
								// We intentionally omit `skipNextFetch` here so
								// that if reconnect fails and the conversation returns to `idle`, a subsequent
								// `fetchConversation` call is NOT silenced by the flag and can retry normally.
								// `fetchConversation` is naturally blocked from re-running by the conversation
							},
						}),
					);

					setMessages(conversationId, updatedMessages);
					dispatch(restoreTaskModeForConversation({ conversationId, messages }));

					dispatch(
						reconnectToInProgressStream({
							conversationId,
							messageId,
							isDeepResearch,
						}),
					);

					experienceTracker.succeed({ name: ExperienceName.PREFETCH_CHAT });
					onSuccess?.();
					return;
				}

				dispatch(
					setConversationData({
						conversationId,
						data: {
							...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
							sourceAgentId: getSourceAgentId(
								messages,
								getConversation(getState(), conversationId),
							),
							...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
							...(skipNextFetch ? { skipNextFetch: true } : {}),
						},
					}),
				);

				setMessages(conversationId, messages);
				dispatch(restoreTaskModeForConversation({ conversationId, messages }));

				experienceTracker.succeed({ name: ExperienceName.PREFETCH_CHAT });
				onSuccess?.();
			} catch (error) {
				const normalizedError = error instanceof Error ? error : new Error(String(error));
				experienceTracker.fail({
					name: ExperienceName.PREFETCH_CHAT,
					error: normalizedError,
				});

				if (
					isForbiddenConversationError(error) &&
					fg('rovo_chat_improved_conversation_403_handling')
				) {
					handleForbiddenConversation({
						conversationId,
						dispatch,
						sendAnalyticsEvent,
						showFlag,
					});
					onError?.(normalizedError);
					return;
				}

				// Roll back warm loading state; do not set `skipNextFetch` on failure.
				dispatch(
					setConversationData({
						conversationId,
						data: clearStatus({}),
					}),
				);
				onError?.(normalizedError);
			}
		},
	{
		experienceName: () => ExperienceName.PREFETCH_CHAT,
		actionErrorHandler: () => {
			// Errors are surfaced via `onError`; keep the silent warm path silent.
			return;
		},
	},
);

/**
 * Seeds a conversation into the store without going through createSafeAction.
 *
 * This is used by the template-smartCreate flow where createConversationChannel
 * has already created a real backend conversation. We need to:
 *   1. Register it in state.conversations (so setSelectedConversation can find it)
 *   2. Set it as the active conversation
 *
 * Unlike createConversationOptimistically, this does NOT use createSafeAction,
 * so it works even when ChatStoreContainer is not mounted (e.g. template gallery page).
 */
const seedConversation =
	({
		conversationId,
		skipNextFetch,
	}: {
		conversationId: string;
		/** When true, ChatStoreContainer will skip its initial fetchConversation call.
		 * Use this when messages have already been populated locally (e.g. template flow)
		 * to prevent the fetch from overwriting locally-seeded messages. */
		skipNextFetch?: boolean;
	}): ChatStoreAction =>
	({ getState, setState }) => {
		const time = new Date().toISOString();
		// Register in conversations map
		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...(getState().conversations[conversationId] ?? {}),
					id: conversationId,
					name: generateConversationName(),
					status: CHAT_STORE_STATUS.idle,
					timeCreated: time,
					lastMessageTimestamp: time,
					traces: [],
					messages: [],
					lastMessageType: [],
					...(skipNextFetch ? { skipNextFetch: true } : {}),
				},
			},
			// Also set as active conversation
			currentConversationId: conversationId,
		});
	};

const stopTask = createSafeAction(
	StoreActionLoggableName.stopTask,
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
		({ getState, dispatch }, { assistanceService, unifiedTasksActions }) => {
			if (!fg('rovo_chat_enable_tasks')) {
				return;
			}

			const conversation = getState().conversations[conversationId];
			if (conversation?.storeAbortController) {
				dispatch(abortRequests({ conversationId, taskModeEnabled: true }));
				return;
			}

			if (!unifiedTasksActions) {
				return;
			}

			const task = unifiedTasksActions.getActiveTaskForConversation(conversationId);
			if (!task?.taskId || isTerminalTaskState(task.progressState)) {
				return;
			}

			unifiedTasksActions.setTaskProgressState({
				taskId: task.taskId,
				progressState: 'Cancelled',
				updatedAt: new Date().toISOString(),
			});

			void assistanceService.cancelTask(task.taskId).catch((err: unknown) => {
				logException({
					name: 'rovoCancelTaskFromStopTask',
					exception: err,
					tags: { taskId: task.taskId },
				});
			});
		},
);

const clearTargetTaskMessageId =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	({ dispatch }) => {
		dispatch(setConversationData({ conversationId, data: { targetTaskMessageId: null } }));
	};

/**
 * Checks the selected conversation's unified task after conversations are loaded.
 * If the active task is not in a terminal state (Completed/Cancelled/Failed),
 * restores the chat mode to 'task' so the user resumes in task mode.
 */
const restoreTaskModeIfNeeded =
	(): ChatStoreAction =>
	({ getState }, { setReasoningMode, unifiedTasksActions }) => {
		if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
			return;
		}

		const { currentConversationId } = getState();
		const task = unifiedTasksActions.getActiveTaskForConversation(currentConversationId);
		if (task && !isTerminalTaskState(task.progressState)) {
			setReasoningMode(currentConversationId, 'task');
		}
	};

const actions: {
	abortRequests: (
		args_0?:
			| {
					conversationId?: string;
					deepResearchEnabled?: boolean;
					thinkDeeperEnabled?: boolean;
					fastModeEnabled?: boolean;
					taskModeEnabled?: boolean;
			  }
			| undefined,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	addAgentMessage: (args_0: {
		agentNamedId: string;
		conversationId: string;
		content: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	addErrorMessage: (args_0: {
		error: unknown;
		conversationId?: string;
		errorPrefix?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	answerNow: (args_0: {
		conversationId?: string;
		tmpMessageId: string; // For store operations (finding/updating the message)
		remoteMessageId: string; // For the API call
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	bulkResolveConversationActions: (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		args: BulkResolveConversationActionsArgs<any>,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
	bulkResolveConversationUIActions: (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		args: BulkResolveConversationUIActionsArgs<any>,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
	clearFollowUpMessages: (args_0: {
		conversationId?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	stopTask: (args_0: {
		conversationId: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	clearTargetTaskMessageId: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
	createConversationOptimistically: (options: {
		name?: string;
		sourceId?: string;
		id?: string;
		timeCreated?: string;
		lastMessageTimestamp?: string;
		sourceAgentId?: string;
		status?: CHAT_STORE_STATUS;
		lastMessageType?: MessageType[];
		lastAgentMessageStatus?: LastAgentMessageStatus;
		agent?: AgentMetadata;
		agentVersion?: AgentVersion;
		rovoSpaceId?: string;
		scopes?: ConversationChannelScope[];
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	createSeededConversation: (options: {
		name?: string;
		dialogues: Dialogues[];
		sourceId?: string;
		id?: string;
		followUpSource: Omit<FollowUpSource, 'none'>;
		agentId?: string;
		agentVersion?: AgentVersion;
		tags?: string[];
		skipNextFetch?: boolean;
		// When the seeded conversation is scoped to a Rovo Space (e.g. created from
		// the SpaceLandingPage chat input or a space starter), set this so the
		// conversation appears under the space in the side nav and is excluded from
		// the regular conversation list immediately on optimistic creation.
		rovoSpaceId?: string;
		mode?: {
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			fastModeEnabled?: boolean;
			taskModeEnabled?: boolean;
			webSearchEnabled?: boolean;
			useCurrentPageContext?: boolean;
			appFilters?: RovoApp[];
		};
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	deleteConversation: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
	getChat: (
		args_0: FetchConversationArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	handleChatOpenPubsubAction: (args_0: {
		conversationId: string;
		agentId?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	hydrateConversationWithExternalParams: (args_0: {
		autoSelectConversation: boolean;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	fetchConversationChannelsData: (
		args_0?: FetchConversationChannelsDataArgs | undefined,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	loadMoreConversationChannelsData: () => Action<
		ChatStorePrivateState,
		ChatStoreContainerProps,
		Promise<void>
	>;
	setConversationListMounted: (isConversationListMounted: boolean) => ChatStoreAction;
	setConversationData: ({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Partial<Conversation>;
	}) => ChatStoreAction;
	removeMessage: (args_0: {
		messageId: number;
		conversationId?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	truncateFromLastHumanMessage: (args_0: {
		conversationId?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<StoreChatItem[]>>;
	resolveConversationAction: (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		args: ResolveConversationActionArgs<any>,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
	sendMessage: (
		props: SendMessageConsumerPramas,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	sendMessageConfirmedTools: (args_0: {
		conversationId?: string;
		agentId?: string;
		confirmed_tools: RovoLabsConfirmedTools;
		messageSources?: Partial<MessageSource>;
		storeUserMessages?: boolean;
		webSearchEnabled?: boolean;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
		fastModeEnabled?: boolean;
		taskModeEnabled?: boolean;
		includeAIFeatureContext?: boolean;
		agentVersion?: AgentVersionInput;
		actionedAnalyticsAttributes?: AdditionalAnalyticAttributes;
		resolutionType?: 'confirmed' | 'cancelled';
		productKey?: string;
		addCurrentPageAsContextUserPreference?: boolean;
		browserContextSanitizer?: BrowserContextSanitizer;
		hasStagedContent?: boolean;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	setSelectedConversation: (args_0: {
		conversationId: string;
		sourceAgentId?: string;
		/**
		 * When the id is not yet in `conversations` (e.g. opened from space-scoped GET list),
		 * seed the store from this payload so selection is not downgraded to empty chat.
		 */
		conversationMetadata?: Conversation & { isEmpty?: unknown; currentTask?: unknown };
		/**
		 * When set, ChatHistory will scroll to this specific message after the conversation loads.
		 * Used by Go to Chat from the Tasks List to route to the task's completion/current message.
		 */
		targetMessageId?: string | null;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	updateConversation: ({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Partial<Pick<Conversation, 'name' | 'readStatus' | 'isPinned' | 'pinnedAt'>>;
	}) => ChatStoreAction;
	removeConversationFromStore: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
	reconnectToInProgressStream: (
		args_0: ReconnectToInProgressStreamParams,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	restoreTaskModeIfNeeded: () => ChatStoreAction;
	warmConversationCache: (
		args_0: WarmConversationCacheArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	prefetchConversationMessages: (
		args_0: PrefetchConversationMessagesArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	seedConversation: ({
		conversationId,
		skipNextFetch,
	}: {
		conversationId: string;
		/** When true, ChatStoreContainer will skip its initial fetchConversation call.
		 * Use this when messages have already been populated locally (e.g. template flow)
		 * to prevent the fetch from overwriting locally-seeded messages. */
		skipNextFetch?: boolean;
	}) => ChatStoreAction;
} = {
	abortRequests,
	addAgentMessage,
	addErrorMessage,
	answerNow,
	bulkResolveConversationActions,
	bulkResolveConversationUIActions,
	clearFollowUpMessages,
	stopTask,
	clearTargetTaskMessageId,
	createConversationOptimistically,
	createSeededConversation,
	deleteConversation,
	getChat: fetchConversation,
	handleChatOpenPubsubAction,
	hydrateConversationWithExternalParams,
	fetchConversationChannelsData,
	loadMoreConversationChannelsData,
	setConversationListMounted,
	setConversationData,
	removeMessage,
	truncateFromLastHumanMessage,
	resolveConversationAction,
	sendMessage,
	sendMessageConfirmedTools,
	setSelectedConversation,
	updateConversation,
	removeConversationFromStore,
	reconnectToInProgressStream,
	restoreTaskModeIfNeeded,
	warmConversationCache,
	prefetchConversationMessages,
	seedConversation,
};

const chatStore = createChatStore(actions);

export type ChatStoreActions = typeof actions;

/**
 * ************************************************************
 * Container
 * ************************************************************
 */
const storeInit =
	(): ChatStoreAction =>
	async ({ dispatch }, { setChatStatus, skipAutoConversationSelection }) => {
		addSentryBreadcrumb('ChatStoreContainer: storeInit');
		setChatStatus('launching');

		// Init conversation hydrates the chat store with conversation from URL or Storage
		// and fetches conversations list (however, fetching conversations list is performed "in the background")
		// conversations list is fetched to be later on displayed in the sidebar/conversation list to remove/minimize
		// visual delay when user opens the side-navigation; or in full-screen mode to fetch conversations as early as possible;
		const initConversation = async () => {
			dispatch(
				hydrateConversationWithExternalParams({
					autoSelectConversation: !skipAutoConversationSelection,
				}),
			);
			interactionMetrics.initializeChannels.start();
			await dispatch(fetchConversationChannelsData({ isInitialLoad: true }));
			interactionMetrics.initializeChannels.stop();
		};

		void initConversation();

		addSentryBreadcrumb('ChatStoreContainer: storeInit completed');
	};

const storeInitOnUpdate = (): ChatStoreAction => {
	return () => {};
};

const chatStoreContainerOptions = {
	onInit: storeInit,
	onUpdate: storeInitOnUpdate,
};

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const ChatStoreContainer: OverrideContainerComponent<ChatStoreContainerProps> =
	createContainer<ChatStorePrivateState, ChatStoreActions, ChatStoreContainerProps>(
		chatStore,
		chatStoreContainerOptions,
	);

/**
 * ************************************************************
 * Main Hook
 * ************************************************************
 */

const publicStateSelector = (state: ChatStorePrivateState): ChatStoreState => {
	return {
		...state,
		checkActiveConversationMatchesSource: (id?: string) => {
			return checkActiveConversationMatchesSource(state, id);
		},
	};
};

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const useCurrentConversationId: HookStateFunction<string, void> = createStateHook(
	chatStore,
	{
		selector: (state: ChatStorePrivateState) => state.currentConversationId,
	},
);

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const useCurrentConversation: HookStateFunction<Conversation | undefined, void> =
	createStateHook(chatStore, {
		selector: (state: ChatStorePrivateState) => state.conversations[state.currentConversationId],
	});

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const useConversationById: HookStateFunction<
	Conversation | undefined,
	{ conversationId: string }
> = createStateHook(chatStore, {
	selector: (state: ChatStorePrivateState, { conversationId }) =>
		state.conversations[conversationId],
});

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const useConversations: HookStateFunction<ChatStorePrivateState['conversations'], void> =
	createStateHook(chatStore, {
		selector: (state: ChatStorePrivateState) => state.conversations,
	});

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const useIsConversationsInitialized: HookStateFunction<
	ChatStorePrivateState['isConversationsInitialized'],
	void
> = createStateHook(chatStore, {
	selector: (state: ChatStorePrivateState) => state.isConversationsInitialized,
});

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const useConversationPagination: HookStateFunction<
	ChatStorePrivateState['conversationPagination'],
	void
> = createStateHook(chatStore, {
	selector: (state: ChatStorePrivateState) => state.conversationPagination,
});

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const useChatStore: HookFunction<
	ChatStoreState,
	BoundActions<
		ChatStorePrivateState,
		{
			abortRequests: (
				args_0?:
					| {
							conversationId?: string;
							deepResearchEnabled?: boolean;
							thinkDeeperEnabled?: boolean;
							fastModeEnabled?: boolean;
							taskModeEnabled?: boolean;
					  }
					| undefined,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addAgentMessage: (args_0: {
				agentNamedId: string;
				conversationId: string;
				content: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addErrorMessage: (args_0: {
				error: unknown;
				conversationId?: string;
				errorPrefix?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			answerNow: (args_0: {
				conversationId?: string;
				tmpMessageId: string; // For store operations (finding/updating the message)
				remoteMessageId: string; // For the API call
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			bulkResolveConversationActions: (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				args: BulkResolveConversationActionsArgs<any>,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			bulkResolveConversationUIActions: (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				args: BulkResolveConversationUIActionsArgs<any>,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			clearFollowUpMessages: (args_0: {
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			stopTask: (args_0: {
				conversationId: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			clearTargetTaskMessageId: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
			createConversationOptimistically: (options: {
				name?: string;
				sourceId?: string;
				id?: string;
				timeCreated?: string;
				lastMessageTimestamp?: string;
				sourceAgentId?: string;
				status?: CHAT_STORE_STATUS;
				lastMessageType?: MessageType[];
				lastAgentMessageStatus?: LastAgentMessageStatus;
				agent?: AgentMetadata;
				agentVersion?: AgentVersion;
				rovoSpaceId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			createSeededConversation: (options: {
				name?: string;
				dialogues: Dialogues[];
				sourceId?: string;
				id?: string;
				followUpSource: Omit<FollowUpSource, 'none'>;
				agentId?: string;
				agentVersion?: AgentVersion;
				tags?: string[];
				skipNextFetch?: boolean;
				// When the seeded conversation is scoped to a Rovo Space (e.g. created from
				// the SpaceLandingPage chat input or a space starter), set this so the
				// conversation appears under the space in the side nav and is excluded from
				// the regular conversation list immediately on optimistic creation.
				rovoSpaceId?: string;
				mode?: {
					deepResearchEnabled?: boolean;
					thinkDeeperEnabled?: boolean;
					fastModeEnabled?: boolean;
					taskModeEnabled?: boolean;
					webSearchEnabled?: boolean;
					useCurrentPageContext?: boolean;
					appFilters?: RovoApp[];
				};
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			deleteConversation: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
			getChat: (
				args_0: FetchConversationArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			handleChatOpenPubsubAction: (args_0: {
				conversationId: string;
				agentId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			hydrateConversationWithExternalParams: (args_0: {
				autoSelectConversation: boolean;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			fetchConversationChannelsData: (
				args_0?: FetchConversationChannelsDataArgs | undefined,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			loadMoreConversationChannelsData: () => Action<
				ChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			setConversationListMounted: (isConversationListMounted: boolean) => ChatStoreAction;
			setConversationData: ({
				conversationId,
				data,
			}: {
				conversationId: string;
				data: Partial<Conversation>;
			}) => ChatStoreAction;
			removeMessage: (args_0: {
				messageId: number;
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			truncateFromLastHumanMessage: (args_0: {
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<StoreChatItem[]>>;
			resolveConversationAction: (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				args: ResolveConversationActionArgs<any>,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			sendMessage: (
				props: SendMessageConsumerPramas,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			sendMessageConfirmedTools: (args_0: {
				conversationId?: string;
				agentId?: string;
				confirmed_tools: RovoLabsConfirmedTools;
				messageSources?: Partial<MessageSource>;
				storeUserMessages?: boolean;
				webSearchEnabled?: boolean;
				deepResearchEnabled?: boolean;
				thinkDeeperEnabled?: boolean;
				alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
				fastModeEnabled?: boolean;
				taskModeEnabled?: boolean;
				includeAIFeatureContext?: boolean;
				agentVersion?: AgentVersionInput;
				actionedAnalyticsAttributes?: AdditionalAnalyticAttributes;
				resolutionType?: 'confirmed' | 'cancelled';
				productKey?: string;
				addCurrentPageAsContextUserPreference?: boolean;
				browserContextSanitizer?: BrowserContextSanitizer;
				hasStagedContent?: boolean;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setSelectedConversation: (args_0: {
				conversationId: string;
				sourceAgentId?: string;
				/**
				 * When the id is not yet in `conversations` (e.g. opened from space-scoped GET list),
				 * seed the store from this payload so selection is not downgraded to empty chat.
				 */
				conversationMetadata?: Conversation & { isEmpty?: unknown; currentTask?: unknown };
				/**
				 * When set, ChatHistory will scroll to this specific message after the conversation loads.
				 */
				targetMessageId?: string | null;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			updateConversation: ({
				conversationId,
				data,
			}: {
				conversationId: string;
				data: Partial<Pick<Conversation, 'name' | 'readStatus' | 'isPinned' | 'pinnedAt'>>;
			}) => ChatStoreAction;
			removeConversationFromStore: ({
				conversationId,
			}: {
				conversationId: string;
			}) => ChatStoreAction;
			reconnectToInProgressStream: (
				args_0: ReconnectToInProgressStreamParams,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			restoreTaskModeIfNeeded: () => ChatStoreAction;
			warmConversationCache: (
				args_0: WarmConversationCacheArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			prefetchConversationMessages: (
				args_0: PrefetchConversationMessagesArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			seedConversation: ({
				conversationId,
				skipNextFetch,
			}: {
				conversationId: string;
				/** When true, ChatStoreContainer will skip its initial fetchConversation call.
				 * Use this when messages have already been populated locally (e.g. template flow)
				 * to prevent the fetch from overwriting locally-seeded messages. */
				skipNextFetch?: boolean;
			}) => ChatStoreAction;
		}
	>,
	void
> = createHook(chatStore, {
	selector: publicStateSelector,
});

/**
 * This is the preferred hook if component does not need any state, as this will never trigger re-render
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const useChatStoreActions: HookActionsFunction<
	BoundActions<
		ChatStorePrivateState,
		{
			abortRequests: (
				args_0?:
					| {
							conversationId?: string;
							deepResearchEnabled?: boolean;
							thinkDeeperEnabled?: boolean;
							fastModeEnabled?: boolean;
							taskModeEnabled?: boolean;
					  }
					| undefined,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addAgentMessage: (args_0: {
				agentNamedId: string;
				conversationId: string;
				content: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addErrorMessage: (args_0: {
				error: unknown;
				conversationId?: string;
				errorPrefix?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			answerNow: (args_0: {
				conversationId?: string;
				tmpMessageId: string; // For store operations (finding/updating the message)
				remoteMessageId: string; // For the API call
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			bulkResolveConversationActions: (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				args: BulkResolveConversationActionsArgs<any>,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			bulkResolveConversationUIActions: (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				args: BulkResolveConversationUIActionsArgs<any>,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			clearFollowUpMessages: (args_0: {
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			stopTask: (args_0: {
				conversationId: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			clearTargetTaskMessageId: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
			createConversationOptimistically: (options: {
				name?: string;
				sourceId?: string;
				id?: string;
				timeCreated?: string;
				lastMessageTimestamp?: string;
				sourceAgentId?: string;
				status?: CHAT_STORE_STATUS;
				lastMessageType?: MessageType[];
				lastAgentMessageStatus?: LastAgentMessageStatus;
				agent?: AgentMetadata;
				agentVersion?: AgentVersion;
				rovoSpaceId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			createSeededConversation: (options: {
				name?: string;
				dialogues: Dialogues[];
				sourceId?: string;
				id?: string;
				followUpSource: Omit<FollowUpSource, 'none'>;
				agentId?: string;
				agentVersion?: AgentVersion;
				tags?: string[];
				skipNextFetch?: boolean;
				// When the seeded conversation is scoped to a Rovo Space (e.g. created from
				// the SpaceLandingPage chat input or a space starter), set this so the
				// conversation appears under the space in the side nav and is excluded from
				// the regular conversation list immediately on optimistic creation.
				rovoSpaceId?: string;
				mode?: {
					deepResearchEnabled?: boolean;
					thinkDeeperEnabled?: boolean;
					fastModeEnabled?: boolean;
					taskModeEnabled?: boolean;
					webSearchEnabled?: boolean;
					useCurrentPageContext?: boolean;
					appFilters?: RovoApp[];
				};
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			deleteConversation: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
			getChat: (
				args_0: FetchConversationArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			handleChatOpenPubsubAction: (args_0: {
				conversationId: string;
				agentId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			hydrateConversationWithExternalParams: (args_0: {
				autoSelectConversation: boolean;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			fetchConversationChannelsData: (
				args_0?: FetchConversationChannelsDataArgs | undefined,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			loadMoreConversationChannelsData: () => Action<
				ChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			setConversationListMounted: (isConversationListMounted: boolean) => ChatStoreAction;
			setConversationData: ({
				conversationId,
				data,
			}: {
				conversationId: string;
				data: Partial<Conversation>;
			}) => ChatStoreAction;
			removeMessage: (args_0: {
				messageId: number;
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			truncateFromLastHumanMessage: (args_0: {
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<StoreChatItem[]>>;
			resolveConversationAction: (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				args: ResolveConversationActionArgs<any>,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			sendMessage: (
				props: SendMessageConsumerPramas,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			sendMessageConfirmedTools: (args_0: {
				conversationId?: string;
				agentId?: string;
				confirmed_tools: RovoLabsConfirmedTools;
				messageSources?: Partial<MessageSource>;
				storeUserMessages?: boolean;
				webSearchEnabled?: boolean;
				deepResearchEnabled?: boolean;
				thinkDeeperEnabled?: boolean;
				alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
				fastModeEnabled?: boolean;
				taskModeEnabled?: boolean;
				includeAIFeatureContext?: boolean;
				agentVersion?: AgentVersionInput;
				actionedAnalyticsAttributes?: AdditionalAnalyticAttributes;
				resolutionType?: 'confirmed' | 'cancelled';
				productKey?: string;
				addCurrentPageAsContextUserPreference?: boolean;
				browserContextSanitizer?: BrowserContextSanitizer;
				hasStagedContent?: boolean;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setSelectedConversation: (args_0: {
				conversationId: string;
				sourceAgentId?: string;
				/**
				 * When the id is not yet in `conversations` (e.g. opened from space-scoped GET list),
				 * seed the store from this payload so selection is not downgraded to empty chat.
				 */
				conversationMetadata?: Conversation & { isEmpty?: unknown; currentTask?: unknown };
				/**
				 * When set, ChatHistory will scroll to this specific message after the conversation loads.
				 */
				targetMessageId?: string | null;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			updateConversation: ({
				conversationId,
				data,
			}: {
				conversationId: string;
				data: Partial<Pick<Conversation, 'name' | 'readStatus' | 'isPinned' | 'pinnedAt'>>;
			}) => ChatStoreAction;
			removeConversationFromStore: ({
				conversationId,
			}: {
				conversationId: string;
			}) => ChatStoreAction;
			reconnectToInProgressStream: (
				args_0: ReconnectToInProgressStreamParams,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			restoreTaskModeIfNeeded: () => ChatStoreAction;
			warmConversationCache: (
				args_0: WarmConversationCacheArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			prefetchConversationMessages: (
				args_0: PrefetchConversationMessagesArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			seedConversation: ({
				conversationId,
				skipNextFetch,
			}: {
				conversationId: string;
				/** When true, ChatStoreContainer will skip its initial fetchConversation call.
				 * Use this when messages have already been populated locally (e.g. template flow)
				 * to prevent the fetch from overwriting locally-seeded messages. */
				skipNextFetch?: boolean;
			}) => ChatStoreAction;
		}
	>
> = createActionsHook(chatStore);
